import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  X, Plus, Video, Image, Sparkle, ArrowClockwise, Download,
  InstagramLogo, LinkedinLogo, FacebookLogo, TiktokLogo, PinterestLogo, WhatsappLogo, Globe,
  CalendarBlank, PaperPlaneTilt, Copy, FloppyDisk, Eye, CheckCircle, Check,
  XCircle, Images, CaretLeft, CaretRight, PencilSimple,
  TextB, TextItalic, TextUnderline, ListBullets, ArrowSquareOut, GearSix
} from '@phosphor-icons/react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { richTextToPlainText, normalizeRichTextForEditor, resolveAssetUrl } from '../lib/utils';
import ContentRegenerationModal from './content-detail/ContentRegenerationModal';
import ContextHelpButton from './ContextHelpButton';

const CAROUSEL_STYLE_PRESETS = [
  { id: 'elegant', tone: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', accent: '#334155' },
  { id: 'minimal', tone: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)', accent: '#0f172a' },
  { id: 'bold', tone: 'linear-gradient(135deg, #0f172a 0%, #7c3aed 100%)', accent: '#ffffff' },
  { id: 'lowtone', tone: 'linear-gradient(135deg, #1f2937 0%, #4b5563 100%)', accent: '#f9fafb' },
  { id: 'editorial', tone: 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)', accent: '#4c1d95' },
  { id: 'luxury', tone: 'linear-gradient(135deg, #111827 0%, #bfa46f 100%)', accent: '#fef3c7' },
];

// CAROUSEL-PUBLIC-PRESETS-001: the ONLY four choices the Design Document
// Carousel panel ever shows or sends — a closed public allowlist mirrored
// exactly on the backend (PublicCarouselPresetKey in creative_models.py)
// and the renderer (TEMPLATE_CATALOG's preset_key). Never admin_template,
// never a legacy style (Elegant/Lowtone/Luxury), never a technical
// template id.
const DESIGN_DOCUMENT_CAROUSEL_PRESETS = ['auto', 'minimal', 'bold', 'editorial', 'elegant'];

// CAROUSEL-TEMPLATE-SPEC-001: one representative preview per selectable
// style, built from the SAME fixed tokens docs/CAROUSEL_TEMPLATE_SPEC.md
// assigns each renderer template (and, for "auto", the internal
// editorial engine's own dark/gold palette) — never a generic gradient,
// so every card is a genuine (if miniature) preview of what that style
// actually renders, restoring the previews the frontend regression had
// dropped for this picker.
const DESIGN_DOCUMENT_CAROUSEL_PREVIEWS = {
  auto: { background: '#050505', ink: '#EFE7DB', accent: '#B08A45', font: '"Cormorant Garamond", Georgia, serif', align: 'left' },
  minimal: { background: '#F7F6F2', ink: '#161614', accent: null, font: '"Space Grotesk", "Helvetica Neue", sans-serif', align: 'left' },
  bold: { background: '#0B0B0B', ink: '#F5F4EF', accent: '#D4FF3D', font: '"Archivo Black", Impact, sans-serif', align: 'left' },
  editorial: { background: '#F3EEE4', ink: '#201C18', accent: '#7A2E2E', font: '"Fraunces", Georgia, serif', align: 'left' },
  elegant: { background: '#EDE6DA', ink: '#2B2620', accent: '#B08D57', font: '"Cormorant Garamond", Georgia, serif', align: 'center' },
};

// CAROUSEL-DARK-MODE-001: the "other" palette for each preset when the
// background/light-dark toggle is flipped away from that preset's own
// default — hex values mirror the renderer templates exactly (see
// renderer/design-document/templates/*.js). "auto" has no alternate:
// it's the internal signature engine, background_mode is a no-op there.
// Bold is inverted on purpose — its DEFAULT (above) is already dark, so
// its "alternate" palette here is the LIGHT one, not another dark one.
const DESIGN_DOCUMENT_CAROUSEL_PREVIEWS_ALT = {
  minimal: { background: '#161614', ink: '#F7F6F2', accent: null, font: '"Space Grotesk", "Helvetica Neue", sans-serif', align: 'left' },
  bold: { background: '#F5F4EF', ink: '#0B0B0B', accent: '#D4FF3D', font: '"Archivo Black", Impact, sans-serif', align: 'left' },
  editorial: { background: '#201C18', ink: '#F3EEE4', accent: '#7A2E2E', font: '"Fraunces", Georgia, serif', align: 'left' },
  elegant: { background: '#2B2620', ink: '#EDE6DA', accent: '#B08D57', font: '"Cormorant Garamond", Georgia, serif', align: 'center' },
};

// Bold's own default look is dark, so for Bold the toggle value "light"
// selects its ALT (light) palette and "dark" selects its default — the
// opposite of every other preset. Mirrors the same isLight branch in
// renderer/design-document/templates/bold-poster-v1.js.
function previewForBackgroundMode(presetKey, backgroundMode) {
  const base = DESIGN_DOCUMENT_CAROUSEL_PREVIEWS[presetKey];
  const alt = DESIGN_DOCUMENT_CAROUSEL_PREVIEWS_ALT[presetKey];
  if (!alt) return base;
  const wantsAlt = presetKey === 'bold' ? backgroundMode === 'light' : backgroundMode === 'dark';
  return wantsAlt ? alt : base;
}

const HEYGEN_FEATURE_ENABLED = String(process.env.REACT_APP_ENABLE_HEYGEN || '').toLowerCase() === 'true';

// DESIGN-DOCUMENT-CAROUSEL-FRONTEND-WIRING-001: read per-render (not
// memoized at module scope like HEYGEN_FEATURE_ENABLED above) purely so
// tests can flip process.env.REACT_APP_ENABLE_DESIGN_DOCUMENT_CAROUSEL_RENDER
// per-case without re-requiring the whole module graph (which would
// otherwise load a second React instance and break the hooks dispatcher).
// CRA's build still inlines this env var as a compile-time constant in
// production either way, so runtime behavior is unaffected.
function isDesignDocumentCarouselRenderEnabled() {
  return String(process.env.REACT_APP_ENABLE_DESIGN_DOCUMENT_CAROUSEL_RENDER || '').toLowerCase() === 'true';
}

// DESIGN-DOCUMENT-CAROUSEL-FRONTEND-WIRING-001: converts one uploaded
// carousel reference asset (its "id" IS the Media Library media_id the
// backend resolves against content.media/carousel_reference_assets)
// into the new endpoint's asset shape — never a style/template/preset,
// only which slide an already-selected asset goes on and its focal
// point when the asset actually carries one.
function buildDesignDocumentCarouselAsset(asset, totalSlides) {
  const rawSlide = asset.slide_position === 'last'
    ? totalSlides
    : (parseInt(asset.slide_position, 10) || 1);
  const resolvedSlide = Math.min(totalSlides, Math.max(1, rawSlide));
  const built = { media_id: asset.id, slide_numbers: [resolvedSlide] };
  if (asset.focal_point && typeof asset.focal_point.x === 'number' && typeof asset.focal_point.y === 'number') {
    built.focal_point_x = asset.focal_point.x;
    built.focal_point_y = asset.focal_point.y;
  }
  return built;
}

function parseCarouselSlidesFromEditableScript(script) {
  const text = String(script || '').trim();
  if (!text) return [];
  const separatorSlides = text.split(/\n\s*-{3,}\s*\n/g).map(part => part.trim()).filter(Boolean);
  if (separatorSlides.length > 1) return separatorSlides;
  const labeledSlides = text.split(/(?=^\s*SLIDE\s+\d+\s*:)/gim).map(part => part.trim()).filter(Boolean);
  return labeledSlides.length > 1 ? labeledSlides : [];
}

function RichCaption({ value, onChange }) {
  const { t } = useTranslation();
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: normalizeRichTextForEditor(value || ''),
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    const normalized = normalizeRichTextForEditor(value || '');
    if (editor && normalized !== editor.getHTML()) {
      editor.commands.setContent(normalized, false);
    }
  }, [value, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) return null;
  const btn = (active, action, Icon, title) => (
    <button type="button" title={title} onMouseDown={e => { e.preventDefault(); action(); }}
      className="p-1.5 rounded transition-colors"
      style={{ background: active ? 'rgba(99,102,241,0.25)' : 'transparent', color: active ? 'var(--accent-purple)' : 'var(--text-muted)' }}>
      <Icon size={14} weight={active ? 'fill' : 'regular'} />
    </button>
  );
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b" style={{ borderColor: 'var(--border-color)', background: 'rgba(255,255,255,0.03)' }}>
        {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), TextB, t('editor.richText.bold'))}
        {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), TextItalic, t('editor.richText.italic'))}
        {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), TextUnderline, t('editor.richText.underline'))}
        <div className="w-px h-4 mx-1" style={{ background: 'var(--border-color)' }} />
        {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), ListBullets, t('editor.richText.bulletList'))}
      </div>
      <EditorContent editor={editor} className="rich-caption-editor" />
    </div>
  );
}

const PLATFORM_ICONS = {
  instagram: { Icon: InstagramLogo, color: '#E4405F', name: 'Instagram' },
  facebook: { Icon: FacebookLogo, color: '#1877F2', name: 'Facebook' },
  linkedin: { Icon: LinkedinLogo, color: '#0A66C2', name: 'LinkedIn' },
  tiktok: { Icon: TiktokLogo, color: '#ffffff', name: 'TikTok' },
  pinterest: { Icon: PinterestLogo, color: '#E60023', name: 'Pinterest' },
};

const getGeneratedMediaLogoState = (media) => {
  if (!media || !media.source_model) return null;
  if (media.brand_logo_applied) return 'applied';
  if (media.brand_logo_error) return 'failed';
  return null;
};

const CanvaIcon = ({ size = 16 }) => (
  <img src="https://www.canva.com/favicon.ico" alt="Canva" style={{ width: size, height: size, borderRadius: 3, objectFit: 'cover' }} />
);

const SMART_SCHEDULE_SLOTS = {
  instagram: ['12:30', '18:30', '21:00'],
  facebook: ['12:30', '18:30'],
  linkedin: ['08:00', '12:00'],
  pinterest: ['18:00', '21:00'],
  tiktok: ['18:00', '21:30'],
};

const getLocaleTag = (lang) => {
  const base = String(lang || '').toLowerCase();
  if (base.startsWith('it')) return 'it-IT';
  if (base.startsWith('en')) return 'en-US';
  if (base.startsWith('es')) return 'es-ES';
  if (base.startsWith('fr')) return 'fr-FR';
  return lang || 'it-IT';
};

const capitalize = (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

function calDays(monthStart) {
  const y = monthStart.getFullYear(), m = monthStart.getMonth();
  const pad = (new Date(y, m, 1).getDay() + 6) % 7;
  const total = new Date(y, m + 1, 0).getDate();
  const cells = Array(pad).fill(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  return cells;
}

function calDaysExtended(monthStart) {
  const y = monthStart.getFullYear(), m = monthStart.getMonth();
  const firstDow = (new Date(y, m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const prevY = m === 0 ? y - 1 : y, prevM = m === 0 ? 11 : m - 1;
  const daysInPrev = new Date(prevY, prevM + 1, 0).getDate();
  const nextY = m === 11 ? y + 1 : y, nextM = m === 11 ? 0 : m + 1;
  const cells = [];
  for (let i = firstDow - 1; i >= 0; i--)
    cells.push({ day: daysInPrev - i, month: prevM, year: prevY, isPrev: true });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ day: d, month: m, year: y, isCurrent: true });
  let nd = 1;
  while (cells.length % 7 !== 0)
    cells.push({ day: nd++, month: nextM, year: nextY, isNext: true });
  return cells;
}

function formatScheduleHeader(dateStr, locale, t) {
  if (!dateStr) return t('editor.selectDate');
  const d = new Date(dateStr + 'T12:00:00');
  const weekday = capitalize(new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d));
  const month = capitalize(new Intl.DateTimeFormat(locale, { month: 'short' }).format(d));
  return `${weekday}, ${month} ${d.getDate()}`;
}

function isSameCalendarDay(dateLike, reference) {
  const date = new Date(dateLike);
  return (
    date.getFullYear() === reference.getFullYear()
    && date.getMonth() === reference.getMonth()
    && date.getDate() === reference.getDate()
  );
}

function isGeneratedCarouselMedia(item) {
  return (
    item?.generated_for_content === 'carousel'
    || item?.carousel_slide_index !== undefined
    || String(item?.original_name || '').toLowerCase().startsWith('carousel slide')
  );
}

function whatsappStatusMediaForContent(content) {
  const images = (content?.media || []).filter(item => item?.type === 'image' || isGeneratedCarouselMedia(item));
  const generated = images.filter(isGeneratedCarouselMedia);
  return generated.length > 0 ? generated : images;
}

function connectorToolArguments(schema = {}, context = {}) {
  const properties = schema.properties || {};
  return Object.fromEntries(Object.entries(properties).map(([key, definition]) => {
    if (definition.default !== undefined) return [key, definition.default];
    if (definition.examples?.length) return [key, definition.examples[0]];

    const normalizedKey = key.toLowerCase();
    const enumValues = definition.enum || [];
    if (enumValues.length) {
      const portraitValue = enumValues.find(value => ['9:16', 'portrait', 'vertical'].includes(String(value).toLowerCase()));
      return [key, portraitValue ?? enumValues[0]];
    }
    if (/aspect|ratio|orientation/.test(normalizedKey)) return [key, '9:16'];
    if (/script|speech|narration|input_text|inputtext/.test(normalizedKey)) return [key, context.script || ''];
    if (/^text$|content|voice_text|voicetext/.test(normalizedKey)) return [key, context.script || ''];
    if (/title|name|headline/.test(normalizedKey)) return [key, context.title || ''];
    if (/prompt|description|direction|visual/.test(normalizedKey)) return [key, context.visualDirection || context.script || ''];
    if (/language|locale/.test(normalizedKey)) return [key, context.language || 'it'];
    if (definition.type === 'boolean') return [key, false];
    if (definition.type === 'number' || definition.type === 'integer') return [key, 0];
    if (definition.type === 'array') return [key, []];
    if (definition.type === 'object') return [key, {}];
    return [key, ''];
  }));
}

function preferredHeyGenTool(tools = []) {
  const scored = tools.map(tool => {
    const haystack = `${tool.name || ''} ${tool.description || ''}`.toLowerCase();
    let score = 0;
    if (haystack.includes('avatar')) score += 8;
    if (haystack.includes('video')) score += 5;
    if (haystack.includes('generate') || haystack.includes('create')) score += 3;
    if (haystack.includes('status') || haystack.includes('translate')) score -= 4;
    return { tool, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.tool || tools[0] || null;
}

function resultUrls(value, urls = new Set()) {
  if (typeof value === 'string') {
    const matches = value.match(/https?:\/\/[^\s"'<>\\]+/g) || [];
    matches.forEach(url => urls.add(url.replace(/[),.;]+$/, '')));
  } else if (Array.isArray(value)) {
    value.forEach(item => resultUrls(item, urls));
  } else if (value && typeof value === 'object') {
    Object.values(value).forEach(item => resultUrls(item, urls));
  }
  return Array.from(urls);
}

function localDateKey(dateLike) {
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function timeToMinutes(time) {
  const [hours, minutes] = String(time || '00:00').split(':').map(v => Number(v) || 0);
  return hours * 60 + minutes;
}

function minutesToTime(totalMinutes) {
  const safe = ((totalMinutes % 1440) + 1440) % 1440;
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function nextRoundedTime(reference = new Date()) {
  const next = new Date(reference);
  next.setSeconds(0, 0);
  next.setMinutes(next.getMinutes() + 30);
  const minutes = next.getMinutes();
  const rounded = minutes <= 30 ? 30 : 60;
  if (rounded === 60) {
    next.setHours(next.getHours() + 1, 0, 0, 0);
  } else {
    next.setMinutes(30, 0, 0);
  }
  return minutesToTime(next.getHours() * 60 + next.getMinutes());
}

function getPlatformSuggestedTime(platform, scheduleDate, reference = new Date()) {
  const slots = SMART_SCHEDULE_SLOTS[platform] || ['10:00'];
  const selectedDate = scheduleDate ? new Date(`${scheduleDate}T12:00:00`) : reference;
  if (!isSameCalendarDay(selectedDate, reference)) return slots[0];
  const nowMinutes = reference.getHours() * 60 + reference.getMinutes();
  const nextPreferred = slots.find(slot => timeToMinutes(slot) > nowMinutes);
  return nextPreferred || nextRoundedTime(reference);
}

function getUnifiedSuggestedTime(platforms, scheduleDate, reference = new Date()) {
  const normalizedPlatforms = platforms.length > 0 ? platforms : ['default'];
  const candidates = [...new Set(
    normalizedPlatforms.flatMap(platform => SMART_SCHEDULE_SLOTS[platform] || ['10:00'])
  )].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));
  const selectedDate = scheduleDate ? new Date(`${scheduleDate}T12:00:00`) : reference;
  const nowMinutes = reference.getHours() * 60 + reference.getMinutes();
  const validCandidates = isSameCalendarDay(selectedDate, reference)
    ? candidates.filter(candidate => timeToMinutes(candidate) > nowMinutes)
    : candidates;

  if (validCandidates.length === 0) return nextRoundedTime(reference);

  const scoreCandidate = (candidate) => normalizedPlatforms.reduce((score, platform) => {
    const slots = SMART_SCHEDULE_SLOTS[platform] || ['10:00'];
    const index = slots.indexOf(candidate);
    if (index === -1) return score;
    return score + (slots.length - index);
  }, 0);

  return validCandidates.reduce((best, candidate) => {
    if (!best) return candidate;
    const bestScore = scoreCandidate(best);
    const currentScore = scoreCandidate(candidate);
    if (currentScore !== bestScore) return currentScore > bestScore ? candidate : best;
    return timeToMinutes(candidate) < timeToMinutes(best) ? candidate : best;
  }, null);
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function SocialMockup({ prof, format, caption, hashtags, media, backendUrl, t }) {
  const pi = PLATFORM_ICONS[prof.platform] || { Icon: Globe, color: '#888', name: prof.platform };
  const firstImg = (media || []).find(m => m.type === 'image');
  const firstVideo = (media || []).find(m => m.type === 'video');
  const isReel = format === 'reel' || format === 'prompted_reel' || prof.platform === 'tiktok';
  const short = (s, n) => s.length > n ? s.slice(0, n) + '…' : s;
  const initials = (prof.profile_name || '?')[0].toUpperCase();
  const captionText = richTextToPlainText(caption);

  /* ── REEL / TIKTOK ── */
  if (isReel) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 160, height: 284, borderRadius: 16, overflow: 'hidden', position: 'relative', background: '#000', border: '2px solid #333', flexShrink: 0 }}>
          {firstVideo ? (
            <video
              src={resolveAssetUrl(firstVideo.url, backendUrl)}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              muted
              playsInline
              preload="metadata"
            />
          ) : firstImg
            ? <img src={resolveAssetUrl(firstImg.url, backendUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(160deg,#1a1535,#2d1f5e)' }} />}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 55%)' }} />
          {/* Right actions */}
          <div style={{ position: 'absolute', right: 6, bottom: 70, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            {['♡','💬','↗'].map((ic, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 16 }}>{ic}</div>
                {i < 2 && <div style={{ color: 'white', fontSize: 8 }}>0</div>}
              </div>
            ))}
          </div>
          {/* Bottom left */}
          <div style={{ position: 'absolute', bottom: 8, left: 8, right: 30 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: `linear-gradient(135deg,${pi.color},#ec4899)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ color: 'white', fontSize: 8, fontWeight: 700 }}>{initials}</span>
              </div>
              <span style={{ color: 'white', fontSize: 9, fontWeight: 600 }}>@{short(prof.profile_name, 14)}</span>
            </div>
            <p style={{ color: 'white', fontSize: 9, lineHeight: 1.35, margin: 0, whiteSpace: 'pre-line' }}>{short(captionText, 80)}</p>
            {hashtags && <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 8, margin: '2px 0 0' }}>{short(hashtags, 40)}</p>}
          </div>
          {/* Platform watermark */}
          <div style={{ position: 'absolute', top: 6, right: 6 }}>
            <pi.Icon size={12} color="white" weight="fill" />
          </div>
        </div>
      </div>
    );
  }

  /* ── PINTEREST ── */
  if (prof.platform === 'pinterest') {
    return (
      <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', color: '#111', fontFamily: 'system-ui,sans-serif' }}>
        <div style={{ position: 'relative', aspectRatio: '2/3', background: '#f0f0f0' }}>
          {firstImg
            ? <img src={resolveAssetUrl(firstImg.url, backendUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PinterestLogo size={32} color="#ccc" />
              </div>}
          <button style={{ position: 'absolute', top: 8, right: 8, background: '#e60023', color: 'white', border: 'none', borderRadius: 20, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'default' }}>{t('editor.socialActionSave')}</button>
        </div>
        <div style={{ padding: '8px 10px 10px' }}>
          <p style={{ fontSize: 12, fontWeight: 700, margin: '0 0 4px', lineHeight: 1.3, whiteSpace: 'pre-line' }}>{short(captionText, 60)}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#e60023', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontSize: 8, fontWeight: 700 }}>{initials}</span>
            </div>
            <span style={{ fontSize: 10, color: '#555' }}>{prof.profile_name}</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── LINKEDIN ── */
  if (prof.platform === 'linkedin') {
    return (
      <div style={{ background: 'white', borderRadius: 8, overflow: 'hidden', color: '#000', fontFamily: 'system-ui,sans-serif', border: '1px solid #ddd' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', padding: '10px 12px 6px', gap: 8 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: '#0a66c2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: 'white', fontSize: 13, fontWeight: 700 }}>{initials}</span>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 12, fontWeight: 700, margin: 0 }}>{prof.profile_name}</p>
            <p style={{ fontSize: 10, color: '#666', margin: 0 }}>{t('editor.oneHourAgoUpper').toLowerCase()} · 🌐</p>
          </div>
          <span style={{ color: '#666', fontSize: 16, cursor: 'default' }}>···</span>
        </div>
        <div style={{ padding: '0 12px 8px' }}>
          <p style={{ fontSize: 11, lineHeight: 1.5, margin: 0, whiteSpace: 'pre-line' }}>{short(captionText, 180)}</p>
          {hashtags && <p style={{ fontSize: 10, color: '#0a66c2', margin: '4px 0 0' }}>{short(hashtags, 80)}</p>}
        </div>
        {(firstVideo || firstImg) && (
          <div style={{ aspectRatio: '1.91/1', overflow: 'hidden' }}>
            {firstVideo ? (
              <video
                src={resolveAssetUrl(firstVideo.url, backendUrl)}
                style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
                muted
                playsInline
                preload="metadata"
              />
            ) : (
              <img src={resolveAssetUrl(firstImg.url, backendUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
        )}
        <div style={{ padding: '6px 12px', borderTop: '1px solid #eee' }}>
          <div style={{ display: 'flex', gap: 2, fontSize: 13, marginBottom: 4 }}>👍💡❤️<span style={{ fontSize: 10, color: '#666', marginLeft: 4, lineHeight: '20px' }}>43</span></div>
          <div style={{ display: 'flex', borderTop: '1px solid #eee', paddingTop: 5 }}>
            {[`👍 ${t('editor.socialActionLike')}`, `💬 ${t('editor.socialActionComment')}`, `↗ ${t('editor.socialActionShare')}`].map((a, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: '#666', padding: '3px 0' }}>{a}</div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── FACEBOOK ── */
  if (prof.platform === 'facebook') {
    return (
      <div style={{ background: 'white', borderRadius: 8, overflow: 'hidden', color: '#000', fontFamily: 'system-ui,sans-serif', border: '1px solid #ddd' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 12px 6px', gap: 8 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1877f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: 'white', fontSize: 13, fontWeight: 700 }}>{initials}</span>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 12, fontWeight: 700, margin: 0 }}>{prof.profile_name}</p>
            <p style={{ fontSize: 10, color: '#65676b', margin: 0 }}>{t('editor.oneHourAgoUpper').toLowerCase()} · 🌐</p>
          </div>
          <span style={{ color: '#606770', fontSize: 16, cursor: 'default' }}>···</span>
        </div>
        <div style={{ padding: '0 12px 8px' }}>
          <p style={{ fontSize: 11, lineHeight: 1.5, margin: 0, color: '#050505', whiteSpace: 'pre-line' }}>{short(captionText, 180)}</p>
          {hashtags && <p style={{ fontSize: 10, color: '#1877f2', margin: '4px 0 0' }}>{short(hashtags, 80)}</p>}
        </div>
        {(firstVideo || firstImg) && (
          <div style={{ aspectRatio: '1.91/1', overflow: 'hidden' }}>
            {firstVideo ? (
              <video
                src={resolveAssetUrl(firstVideo.url, backendUrl)}
                style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
                muted
                playsInline
                preload="metadata"
              />
            ) : (
              <img src={resolveAssetUrl(firstImg.url, backendUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
        )}
        <div style={{ padding: '4px 12px', borderTop: '1px solid #ddd' }}>
          <div style={{ display: 'flex', borderTop: '1px solid #ddd', paddingTop: 6, marginTop: 4 }}>
            {[`👍 ${t('editor.socialActionLike')}`, `💬 ${t('editor.socialActionComment')}`, `↗ ${t('editor.socialActionShare')}`].map((a, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: '#65676b', padding: '3px 0' }}>{a}</div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── INSTAGRAM FEED (default) ── */
  return (
    <div style={{ background: 'white', borderRadius: 8, overflow: 'hidden', color: '#262626', fontFamily: 'system-ui,sans-serif', border: '1px solid #dbdbdb' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', gap: 8 }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ color: 'white', fontSize: 11, fontWeight: 700 }}>{initials}</span>
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 11, fontWeight: 600, margin: 0 }}>{prof.profile_name}</p>
        </div>
        <span style={{ fontSize: 18, color: '#262626' }}>···</span>
      </div>
      <div style={{ aspectRatio: format === 'carousel' ? '4/5' : '1/1', background: '#f3f4f6', overflow: 'hidden' }}>
        {firstVideo ? (
          <video
            src={resolveAssetUrl(firstVideo.url, backendUrl)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
            muted
            playsInline
            preload="metadata"
          />
        ) : firstImg
          ? <img src={resolveAssetUrl(firstImg.url, backendUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <InstagramLogo size={28} color="#dbdbdb" />
            </div>}
      </div>
      <div style={{ padding: '8px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <span style={{ fontSize: 18 }}>♡</span>
          <span style={{ fontSize: 18 }}>🗨</span>
          <span style={{ fontSize: 18 }}>✈</span>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 18 }}>🔖</span>
        </div>
        <p style={{ fontSize: 11, margin: '0 0 3px' }}>
          <span style={{ fontWeight: 700 }}>{prof.profile_name} </span>
          {short(captionText, 120)}
        </p>
        {hashtags && <p style={{ fontSize: 10, color: '#00376b', margin: '2px 0 0' }}>{short(hashtags, 80)}</p>}
        <p style={{ fontSize: 10, color: '#8e8e8e', margin: '4px 0 0' }}>{t('editor.oneHourAgoUpper')}</p>
      </div>
    </div>
  );
}

export default function ContentDetail({ content: initialContent, project, onClose, onUpdate, carouselCopyEditor = null }) {
  const { api, user, effectivePlan } = useAuth();
  const { t, i18n } = useTranslation();
  const locale = getLocaleTag(i18n.language);
  const dayHeaders = Array.from({ length: 7 }, (_, index) =>
    capitalize(new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(2024, 0, 1 + index)))
  );
  const [canvaConnected, setCanvaConnected] = useState(false);
  const [canvaLoading, setCanvaLoading] = useState(false);
  const isMobile = useIsMobile();
  const [content, setContent] = useState(initialContent);
  const [editScript, setEditScript] = useState(initialContent.script || '');
  const [editOpeningHook, setEditOpeningHook] = useState(initialContent.opening_hook || '');
  const [editVisualDirection, setEditVisualDirection] = useState(initialContent.visual_direction || '');
  const [editCaption, setEditCaption] = useState(initialContent.caption || '');
  const [editHashtags, setEditHashtags] = useState(String(initialContent.hashtags || ''));
  const [socialProfiles, setSocialProfiles] = useState([]);
  const [selectedSocials, setSelectedSocials] = useState([]);
  const [tiktokSettings, setTiktokSettings] = useState({});
  const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState({});
  const [openTiktokSettings, setOpenTiktokSettings] = useState({});
  const [loadingTiktokInfo, setLoadingTiktokInfo] = useState({});
  const [publishedSocials, setPublishedSocials] = useState([]);
  const [saving, setSaving] = useState(false);
  // Objective miniwizard (CONTENT-REGEN-004) — dedicated state, separate
  // from `saving`/image-generation/publishing, per the task's requirement
  // not to overload unrelated states.
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState('');
  const regenerateLockRef = useRef(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('10:00');
  const [scheduling, setScheduling] = useState(false);
  const [cancellingSchedule, setCancellingSchedule] = useState(false);
  const [contentQueueItems, setContentQueueItems] = useState([]);
  const [projectQueueItems, setProjectQueueItems] = useState([]);
  const [publishedQueueItems, setPublishedQueueItems] = useState([]);
  const [customPerSocial, setCustomPerSocial] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showWhatsappStatus, setShowWhatsappStatus] = useState(false);
  const [whatsappStatusBusy, setWhatsappStatusBusy] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [deletingMediaId, setDeletingMediaId] = useState(null);
  const [mobileTab, setMobileTab] = useState('editor');
  const [inputModal, setInputModal] = useState(null); // { title, placeholder, value, multiline, onConfirm }
  const [generatingImage, setGeneratingImage] = useState(false);
  const [optimizingPrompt, setOptimizingPrompt] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [calViewDate, setCalViewDate] = useState(null);
  const [imageModel, setImageModel] = useState('openai');
  const [showCarouselStudio, setShowCarouselStudio] = useState(false);
  const [showAiCarouselStudio, setShowAiCarouselStudio] = useState(false);
  const [aiCarouselMode, setAiCarouselMode] = useState('text_images');
  const [carouselStylePreset, setCarouselStylePreset] = useState('elegant');
  const [designDocumentCarouselPreset, setDesignDocumentCarouselPreset] = useState('auto');
  const [carouselSlidesCount, setCarouselSlidesCount] = useState(Math.min(8, Math.max(4, (initialContent.slides || []).length || 6)));
  const [carouselBackgroundMode, setCarouselBackgroundMode] = useState('light');
  const [carouselReferenceAssets, setCarouselReferenceAssets] = useState(initialContent.carousel_reference_assets || []);
  const [uploadingCarouselReference, setUploadingCarouselReference] = useState(false);
  const [projectDesignDnaActive, setProjectDesignDnaActive] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  // CAROUSEL-REFERENCE-DRIVE-LIBRARY-001: 'media' (default) adds the
  // picked item to content.media, exactly as before; 'carousel_reference'
  // instead assigns it to the Carousel Studio's per-slide reference list.
  const [libraryTargetMode, setLibraryTargetMode] = useState('media');
  const [socialTimes, setSocialTimes] = useState({});
  const [markingPublished, setMarkingPublished] = useState(false);
  const [heyGenConnector, setHeyGenConnector] = useState(null);
  const [heyGenBusy, setHeyGenBusy] = useState(false);
  const [heyGenWorkbench, setHeyGenWorkbench] = useState(null);
  const [heyGenSelectedTool, setHeyGenSelectedTool] = useState('');
  const [heyGenArguments, setHeyGenArguments] = useState('{}');
  const [heyGenResult, setHeyGenResult] = useState(null);
  const carouselSlides = Array.isArray(content.slides) ? content.slides.filter(Boolean) : [];
  const whatsappStatusMedia = useMemo(() => whatsappStatusMediaForContent(content), [content]);
  const isAdminCarouselTemplateActive = false;
  // EFFECTIVE-PLAN-SESSION-ONBOARDING-001: a Strategist Power User grant
  // on a Free/Creator base account must unlock premium visual rendering
  // too — this is the effective plan, never the raw base plan.
  const visualPlan = String(effectivePlan || user?.plan || 'free').toLowerCase();
  const hasPremiumVisualRender = user?.role === 'admin'
    || ['strategist', 'custom', 'admin'].includes(visualPlan);
  const enforcedVisualModel = hasPremiumVisualRender ? 'openai' : 'gemini';
  // CAROUSEL-AUTO-VISUAL-DNA-CREATOR-GATE-001: the "auto"/Visual DNA
  // carousel preset (colors + fonts + composition, all DNA-driven) is
  // not part of the Creator plan. Backend enforces this too (403 on
  // preset_key "auto") — this only keeps the picker from ever offering
  // a choice that would be rejected.
  const visualDnaPresetAvailable = hasPremiumVisualRender;
  const availableDesignDocumentCarouselPresets = DESIGN_DOCUMENT_CAROUSEL_PRESETS.filter(
    presetKey => presetKey !== 'auto' || visualDnaPresetAvailable
  );
  const designDocumentCarouselRenderEnabled = isDesignDocumentCarouselRenderEnabled();
  const [dragMediaId, setDragMediaId] = useState(null);

  const scheduledDateIndicators = useMemo(() => {
    const indicators = {};
    (projectQueueItems || []).forEach(item => {
      if (!item.scheduled_at || !['queued', 'processing'].includes(String(item.status || ''))) return;
      const key = localDateKey(item.scheduled_at);
      if (!key) return;
      if (!indicators[key]) {
        indicators[key] = { contentIds: new Set(), otherContentIds: new Set(), platforms: new Set() };
      }
      if (item.content_id) {
        indicators[key].contentIds.add(item.content_id);
        if (item.content_id !== content.id) indicators[key].otherContentIds.add(item.content_id);
      }
      if (item.platform) indicators[key].platforms.add(item.platform);
    });
    return indicators;
  }, [projectQueueItems, content.id]);

  const selectedDateOverlapCount = scheduleDate
    ? (scheduledDateIndicators[scheduleDate]?.otherContentIds?.size || 0)
    : 0;

  const syncQueueState = useCallback((queueData, effectiveStatus = content.status) => {
    const items = (queueData || []).filter(q =>
      q.content_id === initialContent.id
      && ['queued', 'processing', 'failed', 'published'].includes(String(q.status || ''))
    );
    const activeItems = items.filter(q => q.status === 'queued' || q.status === 'processing');
    const publishedItems = items.filter(q => q.status === 'published');
    const publishedSocialIds = [...new Set(publishedItems.map(q => q.social_profile_id).filter(Boolean))];
    const retryableItems = items.filter(
      q => q.status !== 'published' && !publishedSocialIds.includes(q.social_profile_id)
    );
    const retryableSocialIds = [...new Set(retryableItems.map(q => q.social_profile_id).filter(Boolean))];
    const socialSourceItems = activeItems.length > 0
      ? activeItems
      : (retryableItems.length > 0 ? retryableItems : publishedItems);

    setContentQueueItems(activeItems);
    setPublishedQueueItems(publishedItems);
    setPublishedSocials(publishedSocialIds);
    setSelectedSocials(prev => {
      if (retryableSocialIds.length > 0) return retryableSocialIds;
      return prev.filter(id => !publishedSocialIds.includes(id));
    });

    const restoredTikTokSettings = {};
    items.filter(item => item.platform === 'tiktok' && item.platform_settings).forEach(item => {
      restoredTikTokSettings[item.social_profile_id] = item.platform_settings;
    });
    if (Object.keys(restoredTikTokSettings).length > 0) {
      setTiktokSettings(prev => ({ ...prev, ...restoredTikTokSettings }));
    }

    if (socialSourceItems.length > 0) {
      const dt = new Date(socialSourceItems[0].scheduled_at);
      const pad = n => String(n).padStart(2, '0');
      setScheduleDate(`${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`);
      setScheduleTime(`${pad(dt.getHours())}:${pad(dt.getMinutes())}`);
      const times = {};
      socialSourceItems.forEach(q => {
        const d = new Date(q.scheduled_at);
        times[q.social_profile_id] = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      });
      setSocialTimes(times);
    } else {
      setSocialTimes({});
    }
  }, [content.status, initialContent.id]);

  useEffect(() => {
    setContent(initialContent);
    setCarouselReferenceAssets(initialContent.carousel_reference_assets || []);
  }, [initialContent]);

  useEffect(() => {
    if (designDocumentCarouselPreset === 'auto' && !visualDnaPresetAvailable) {
      setDesignDocumentCarouselPreset('minimal');
    }
  }, [designDocumentCarouselPreset, visualDnaPresetAvailable]);

  useEffect(() => {
    api.get(`/social/project/${project.id}`)
      .then(r => setSocialProfiles((r.data || []).filter(p => p.platform !== 'google_slides')))
      .catch(() => setSocialProfiles([]));
    api.get('/canva/status').then(r => setCanvaConnected(r.data.connected)).catch(() => {});
    api.get(`/projects/${project.id}/design`).then(r => {
      setProjectDesignDnaActive(Boolean(r.data?.active_dna));
    }).catch(() => setProjectDesignDnaActive(false));
    api.get(`/publish/queue/${project.id}`).then(r => {
      setProjectQueueItems(r.data || []);
      syncQueueState(r.data, initialContent.status);
    }).catch(() => {});
    if (HEYGEN_FEATURE_ENABLED) {
      api.get(`/projects/${project.id}/creative-studio`).then(r => {
        setHeyGenConnector((r.data?.connectors || []).find(connector => connector.id === 'heygen') || null);
      }).catch(() => setHeyGenConnector(null));
    } else {
      setHeyGenConnector(null);
    }
  }, [api, project.id, initialContent.id, initialContent.status, syncQueueState]);

  useEffect(() => {
    const selectedTikTokProfiles = socialProfiles.filter(
      profile => profile.platform === 'tiktok' && selectedSocials.includes(profile.id)
    );
    selectedTikTokProfiles.forEach(profile => {
      if (tiktokCreatorInfo[profile.id] || loadingTiktokInfo[profile.id]) return;
      setLoadingTiktokInfo(prev => ({ ...prev, [profile.id]: true }));
      api.get(`/social/tiktok/creator-info/${profile.id}`, { params: { project_id: project.id } })
        .then(({ data }) => {
          setTiktokCreatorInfo(prev => ({ ...prev, [profile.id]: data }));
          setTiktokSettings(prev => ({
            ...prev,
            [profile.id]: prev[profile.id] || {
              privacy_level: '',
              allow_comment: false,
              allow_duet: false,
              allow_stitch: false,
              commercial_content: false,
              brand_organic: false,
              brand_content: false,
              is_aigc: false,
              music_usage_confirmed: false,
              upload_consent: false,
            },
          }));
          setOpenTiktokSettings(prev => ({ ...prev, [profile.id]: true }));
        })
        .catch(error => toast.error(`TikTok: ${error.response?.data?.detail || error.message}`))
        .finally(() => setLoadingTiktokInfo(prev => ({ ...prev, [profile.id]: false })));
    });
  }, [api, project.id, selectedSocials, socialProfiles, tiktokCreatorInfo, loadingTiktokInfo]);

  useEffect(() => {
    const handleHeyGenMessage = async event => {
      if (!HEYGEN_FEATURE_ENABLED) return;
      if (event.data?.connector_id !== 'heygen') return;
      if (event.data?.type === 'mcp_connector_success') {
        toast.success(t('editor.heygenConnected'));
        setHeyGenBusy(false);
        try {
          const { data } = await api.get(`/projects/${project.id}/creative-studio`);
          const connector = (data.connectors || []).find(item => item.id === 'heygen');
          setHeyGenConnector(connector || null);
          if (connector) await openHeyGenWorkbench(connector);
        } catch (error) {
          toast.error(t('editor.heygenError', { message: error.response?.data?.detail || error.message }));
        }
      }
      if (event.data?.type === 'mcp_connector_error') {
        setHeyGenBusy(false);
        const message = event.data.error === 'provider_grant_failed'
          ? t('editor.heygenGrantFailed')
          : event.data.error || 'OAuth';
        toast.error(t('editor.heygenError', { message }));
      }
    };
    window.addEventListener('message', handleHeyGenMessage);
    return () => window.removeEventListener('message', handleHeyGenMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, project.id, t, editScript, editOpeningHook, editVisualDirection]);

  useEffect(() => {
    if (content.status !== 'scheduled') return;

    const refreshStatus = async () => {
      try {
        const [{ data: queueData }, { data: contentsData }] = await Promise.all([
          api.get(`/publish/queue/${project.id}`),
          api.get(`/contents/${project.id}`),
        ]);

        setProjectQueueItems(queueData || []);
        const queueItems = (queueData || []).filter(q => q.content_id === content.id);

        const latestContent = (contentsData || []).find(c => c.id === content.id);
        syncQueueState(queueItems, latestContent?.status || content.status);
        if (latestContent && latestContent.status && latestContent.status !== content.status) {
          setContent(prev => {
            const updated = { ...prev, status: latestContent.status };
            onUpdate?.(updated);
            return updated;
          });
        }
      } catch {}
    };

    refreshStatus();
    const interval = setInterval(refreshStatus, 5000);
    return () => clearInterval(interval);
  }, [api, project.id, content.id, content.status, onUpdate, syncQueueState]);

  useEffect(() => {
    if (showSchedule) {
      const base = scheduleDate ? new Date(scheduleDate + 'T12:00:00') : new Date();
      setCalViewDate(new Date(base.getFullYear(), base.getMonth(), 1));
      const times = {};
      selectedSocials.forEach(id => { times[id] = socialTimes[id] || scheduleTime; });
      setSocialTimes(times);
      api.get(`/publish/queue/${project.id}`).then(r => {
        setProjectQueueItems(r.data || []);
        syncQueueState(r.data, content.status);
      }).catch(() => {});
    }
  }, [showSchedule]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (imageModel !== enforcedVisualModel) {
      setImageModel(enforcedVisualModel);
    }
  }, [enforcedVisualModel, imageModel]);

  const reorderContentMedia = useCallback(async (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const mediaItems = [...(content.media || [])];
    const fromIndex = mediaItems.findIndex(item => item.id === fromId);
    const toIndex = mediaItems.findIndex(item => item.id === toId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

    const reordered = [...mediaItems];
    const [movedItem] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, movedItem);

    const updated = { ...content, media: reordered };
    setContent(updated);
    onUpdate?.(updated);

    try {
      await api.put(`/contents/${content.id}`, { media: reordered });
    } catch (e) {
      setContent(content);
      onUpdate?.(content);
      toast.error(t('editor.uploadError', { message: e.response?.data?.detail || e.message }));
    }
  }, [api, content, onUpdate, t]);

  const heyGenContext = () => ({
    script: [editOpeningHook, editScript].filter(Boolean).join('\n\n'),
    title: content.hook_text || editOpeningHook,
    visualDirection: editVisualDirection,
    language: String(i18n.language || 'it').split('-')[0],
  });

  const selectHeyGenTool = (toolName, tools = heyGenWorkbench?.tools || []) => {
    const tool = tools.find(item => item.name === toolName);
    setHeyGenSelectedTool(toolName);
    setHeyGenArguments(JSON.stringify(connectorToolArguments(tool?.inputSchema, heyGenContext()), null, 2));
    setHeyGenResult(null);
  };

  const openHeyGenWorkbench = async (connector = heyGenConnector) => {
    setHeyGenBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/creative-studio/connectors/heygen/discover`);
      const tools = data.tools || [];
      const selectedTool = preferredHeyGenTool(tools);
      setHeyGenWorkbench({ ...(connector || { id: 'heygen', name: 'HeyGen' }), tools });
      setHeyGenSelectedTool(selectedTool?.name || '');
      setHeyGenArguments(JSON.stringify(connectorToolArguments(selectedTool?.inputSchema, heyGenContext()), null, 2));
      setHeyGenResult(null);
    } catch (error) {
      toast.error(t('editor.heygenError', { message: error.response?.data?.detail || error.message }));
    } finally {
      setHeyGenBusy(false);
    }
  };

  const handleHeyGenClick = async () => {
    setHeyGenBusy(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/creative-studio`);
      const connector = (data.connectors || []).find(item => item.id === 'heygen');
      setHeyGenConnector(connector || null);
      if (!connector) throw new Error(t('editor.heygenUnavailable'));
      if (connector.status === 'connected') {
        await openHeyGenWorkbench(connector);
        return;
      }
      if (!connector.can_connect) {
        if (connector.provider_action_url) window.open(connector.provider_action_url, '_blank', 'noopener,noreferrer');
        throw new Error(t('editor.heygenSetupRequired'));
      }
      const { data: authData } = await api.get(`/projects/${project.id}/creative-studio/connectors/heygen/connect`);
      const popup = window.open(authData.auth_url, 'mcp-heygen', 'width=620,height=760,resizable=yes,scrollbars=yes');
      if (!popup) throw new Error(t('editor.heygenPopupBlocked'));
    } catch (error) {
      setHeyGenBusy(false);
      toast.error(t('editor.heygenError', { message: error.response?.data?.detail || error.message }));
    }
  };

  const executeHeyGenTool = async () => {
    let parsedArguments;
    try {
      parsedArguments = JSON.parse(heyGenArguments || '{}');
    } catch {
      toast.error(t('editor.heygenInvalidJson'));
      return;
    }
    setHeyGenBusy(true);
    setHeyGenResult(null);
    try {
      const { data } = await api.post(
        `/projects/${project.id}/creative-studio/connectors/heygen/execute`,
        { tool_name: heyGenSelectedTool, arguments: parsedArguments, import_images: false }
      );
      setHeyGenResult(data.result || {});
      toast.success(t('editor.heygenStarted'));
    } catch (error) {
      toast.error(t('editor.heygenError', { message: error.response?.data?.detail || error.message }));
    } finally {
      setHeyGenBusy(false);
    }
  };

  // ── CANVA ─────────────────────────────────────────────
  const openCanvaEditor = async () => {
    setCanvaLoading(true);
    const tid = toast.loading(t('canva.creating'));
    // Open named popup immediately (synchronous) to bypass popup blockers
    const popup = window.open('about:blank', 'canva_editor', 'width=1280,height=820,left=100,top=60');
    try {
      const { data } = await api.post('/canva/create-design', { content_id: content.id, format: content.format });
      const { edit_url, design_id } = data;
      if (popup && !popup.closed) popup.location.href = edit_url;
      else window.open(edit_url, 'canva_editor', 'width=1280,height=820,left=100,top=60');
      toast.success(t('canva.opened'), { id: tid, duration: 10000 });

      let importing = false;
      const doImport = async () => {
        if (importing) return;
        importing = true;
          const importTid = toast.loading(t('canva.importing'));
        try {
          // Step 1: create export job (fast, instant response)
          const { data: jobData } = await api.post(`/canva/export-design/${content.id}`, { design_id });
          const { job_id } = jobData;

          // Step 2: poll for completion (frontend-driven, every 3s, max 20 tries)
          toast.loading(t('canva.exporting'), { id: importTid });
          let urls = [];
          for (let i = 0; i < 20; i++) {
            await new Promise(r => setTimeout(r, 3000));
            const { data: statusData } = await api.get(`/canva/export-status/${job_id}`);
            if (statusData.status === 'success') { urls = statusData.urls || []; break; }
            if (statusData.status === 'failed') throw new Error(t('common.error'));
          }

          if (urls.length === 0) { toast.info(t('canva.noImages'), { id: importTid }); return; }

          // Step 3: download client-side (browser→CDN, no Railway timeout) then upload via FormData
          toast.loading(t('canva.downloading'), { id: importTid });
          const newMedia = [];
          for (let i = 0; i < urls.length; i++) {
            const resp = await fetch(urls[i]);
            if (!resp.ok) continue;
            const blob = await resp.blob();
            const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
            const file = new File([blob], `canva_export_${i + 1}.${ext}`, { type: blob.type || 'image/png' });
            const fd = new FormData();
            fd.append('file', file);
            const { data: mediaDoc } = await api.post(`/media/upload/${content.id}`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            newMedia.push(mediaDoc);
          }
          if (newMedia.length > 0) {
            const updated = { ...content, media: [...(content.media || []), ...newMedia] };
            setContent(updated); onUpdate?.(updated);
            toast.success(t('canva.importSuccess_other', { count: newMedia.length }), { id: importTid, duration: 5000 });
          } else {
            toast.info(t('canva.noImages'), { id: importTid });
          }
        } catch (e) {
          if (e.response?.status === 401) {
            setCanvaConnected(false);
            toast.error(t('canva.expired'), { id: importTid, duration: 7000 });
          } else {
            const status = e.response?.status ? ` (${e.response.status})` : '';
            const detail = e.response?.data?.detail || e.message;
            toast.error(t('canva.errorImporting', { status, message: detail }), { id: importTid });
          }
        }
      };

      // Poll for popup closure or same-origin navigation ("Torna a Sketchario")
      const interval = setInterval(() => {
        // When Canva redirects back to our domain, popup.location is accessible (same-origin)
        try {
          if (popup && !popup.closed && popup.location.hostname === window.location.hostname) {
            clearInterval(interval);
            popup.close();
            doImport();
            return;
          }
        } catch (_) { /* popup still on Canva (cross-origin) — expected */ }

        if (!popup || popup.closed) {
          clearInterval(interval);
          doImport();
        }
      }, 800);
    } catch (e) {
      if (popup && !popup.closed) popup.close();
      if (e.response?.status === 401) { setCanvaConnected(false); }
      toast.error(t('canva.errorCreating', { message: e.response?.data?.detail || e.message }), { id: tid });
    }
    setCanvaLoading(false);
  };

  const handleCanvaClick = async () => {
    if (canvaConnected) { await openCanvaEditor(); return; }
    setCanvaLoading(true);
    const popup = window.open('about:blank', 'canva_oauth', 'width=600,height=700,left=200,top=100');
    try {
      const { data } = await api.get('/canva/auth-url');
      if (!data.auth_url) { popup?.close(); setCanvaLoading(false); return; }
      popup.location.href = data.auth_url;
      const handler = async (e) => {
        if (e.data?.type === 'canva_success') {
          window.removeEventListener('message', handler);
          popup?.close();
          setCanvaConnected(true);
          setCanvaLoading(false);
          await openCanvaEditor();
        } else if (e.data?.type === 'canva_error') {
          window.removeEventListener('message', handler);
          popup?.close();
          toast.error(t('editor.connectCanvaError', { message: e.data.error }));
          setCanvaLoading(false);
        }
      };
      window.addEventListener('message', handler);
    } catch (e) {
      popup?.close();
      toast.error(t('canva.errorCreating', { message: e.response?.data?.detail || e.message }));
      setCanvaLoading(false);
    }
  };

  // ── GOOGLE DRIVE PICKER ───────────────────────────────
  const loadGooglePickerApi = () => new Promise((resolve, reject) => {
    if (window.google?.picker) { resolve(); return; }
    if (document.getElementById('gapi-script')) {
      const poll = setInterval(() => { if (window.google?.picker) { clearInterval(poll); resolve(); } }, 100);
      setTimeout(() => { clearInterval(poll); reject(new Error('Timeout')); }, 10000);
      return;
    }
    const script = document.createElement('script');
    script.id = 'gapi-script';
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => window.gapi.load('picker', resolve);
    script.onerror = () => reject(new Error(t('editor.driveLoadError')));
    document.head.appendChild(script);
  });

  // CAROUSEL-REFERENCE-DRIVE-LIBRARY-001: target defaults to 'media' (the
  // original behavior, importing into content.media) - passing
  // 'carousel_reference' instead routes every picked file into the
  // Carousel Studio's per-slide reference-image list
  // (uploadCarouselReferenceAsset, same endpoint the local-file picker
  // already uses) rather than the general content media list, and
  // restricts the accepted mime types to images only (carousel reference
  // assets are never video - CAROUSEL_REFERENCE_ALLOWED_EXTS on the
  // backend already rejects anything else).
  const openDrivePicker = async (target = 'media') => {
    const tid = toast.loading(t('drive.opening'));
    try {
      const { data } = await api.get(`/google/picker-token?project_id=${encodeURIComponent(project.id)}`);
      if (!data.connected) {
        const msg = data.reason === 'scope_upgrade'
          ? t('drive.scopeExpired')
          : t('drive.notConnected');
        toast.error(msg, { id: tid, duration: 7000 });
        return;
      }
      toast.dismiss(tid);
      await loadGooglePickerApi();
      const { PickerBuilder, Action, Feature } = window.google.picker;
      // No MIME filter: filtering causes flat global search instead of tree navigation.
      // Users see the full folder tree and can navigate normally.
      const view = new window.google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setParent('root');
      const pickerToken = data.token;
      new PickerBuilder()
        .addView(view)
        .enableFeature(Feature.MULTISELECT_ENABLED)
        .setOAuthToken(pickerToken)
        .setCallback(async (pickerData) => {
          if (pickerData.action !== Action.PICKED) return;
          // GOOGLE-DRIVE-BULK-UPLOAD-001: multiselect enabled above means
          // pickerData.docs can now contain more than one file — import
          // each one in turn instead of only ever taking docs[0].
          const docs = pickerData.docs || [];
          const allowed = target === 'carousel_reference' ? ['image/'] : ['image/', 'video/'];
          for (const doc of docs) {
            if (!allowed.some(t => (doc.mimeType || '').startsWith(t))) {
              toast.error(target === 'carousel_reference' ? t('editor.carouselReferenceDriveImagesOnly') : t('editor.driveSelectMediaOnly'));
              continue;
            }
            const importTid = toast.loading(t('drive.downloading', { name: doc.name }));
            try {
              // Download client-side with the picker token (avoids server-side scope issues)
              const dlResp = await fetch(
                `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`,
                { headers: { Authorization: `Bearer ${pickerToken}` } }
              );
              if (!dlResp.ok) {
                if (dlResp.status === 403 || dlResp.status === 404) throw new Error(t('drive.errorPermissions'));
                throw new Error(t('editor.downloadRejected', { status: dlResp.status }));
              }
              const blob = await dlResp.blob();
              const file = new File([blob], doc.name, { type: doc.mimeType || blob.type });
              if (target === 'carousel_reference') {
                const defaultPosition = carouselReferenceAssets.length === 0 ? '1' : 'last';
                await uploadCarouselReferenceAsset(file, defaultPosition);
              } else {
                const fd = new FormData();
                fd.append('file', file);
                const { data: media } = await api.post(`/media/upload/${content.id}`, fd);
                // Functional setState: sequential uploads in this loop would
                // otherwise all read the same stale `content` closure and
                // each overwrite the previous file's addition.
                setContent((prev) => {
                  const updated = { ...prev, media: [...(prev.media || []), media] };
                  onUpdate?.(updated);
                  return updated;
                });
              }
              toast.success(t('drive.importSuccess', { name: doc.name }), { id: importTid });
            } catch (e) { toast.error(t('editor.driveImportError', { message: e.message }), { id: importTid }); }
          }
        })
        .build()
        .setVisible(true);
    } catch (e) {
      toast.error(t('drive.error', { message: e.response?.data?.detail || e.message }), { id: tid });
    }
  };

  const openLibrary = async (target = 'media') => {
    setLibraryTargetMode(target);
    setShowLibrary(true);
    if (libraryItems.length > 0) return;
    setLibraryLoading(true);
    try {
      const { data } = await api.get(`/media/library/${project.id}`);
      setLibraryItems(data);
    } catch { toast.error(t('library.errorLoading')); }
    setLibraryLoading(false);
  };

  const addFromLibrary = async (item) => {
    try {
      const { data } = await api.post(`/media/library/add/${content.id}`, { media: item });
      const updated = { ...content, media: [...(content.media || []), data] };
      setContent(updated); onUpdate?.(updated);
      toast.success(t('library.addToContent'));
    } catch { toast.error(t('library.errorAdding')); }
  };

  // CAROUSEL-REFERENCE-DRIVE-LIBRARY-001: a Library item is already stored
  // server-side (previously uploaded or generated), so this re-fetches its
  // bytes client-side (same-origin, already how its thumbnail renders via
  // resolveAssetUrl) and posts them through the exact same
  // uploadCarouselReferenceAsset path the local-file picker uses — no new
  // backend endpoint, no server-side duplication logic to get wrong.
  const addFromLibraryAsCarouselReference = async (item) => {
    const tid = toast.loading(t('editor.carouselReferenceUploading'));
    try {
      const resp = await fetch(resolveAssetUrl(item.url));
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const file = new File([blob], item.original_name || 'library-image', { type: item.mime_type || blob.type });
      const defaultPosition = carouselReferenceAssets.length === 0 ? '1' : 'last';
      await uploadCarouselReferenceAsset(file, defaultPosition);
      toast.success(t('editor.carouselReferenceUploadSuccess'), { id: tid });
      setShowLibrary(false);
    } catch (e) {
      toast.error(t('editor.carouselReferenceUploadError', { message: e.message }), { id: tid });
    }
  };

  const displayedSocialIds = [...new Set([...publishedSocials, ...selectedSocials])];
  const selectedProfiles = socialProfiles.filter(p => displayedSocialIds.includes(p.id));
  const actionableSelectedProfiles = socialProfiles.filter(p => selectedSocials.includes(p.id));
  const hasActiveSchedule = contentQueueItems.length > 0;
  const hasScheduledStatusOnly = content.status === 'scheduled' && !hasActiveSchedule;
  const socialsLocked = hasActiveSchedule && !showSchedule;

  const toggleSocial = (profId) => {
    if (socialsLocked) return;
    setSelectedSocials(prev => prev.includes(profId) ? prev.filter(id => id !== profId) : [...prev, profId]);
  };

  const selectedTikTokProfiles = actionableSelectedProfiles.filter(profile => profile.platform === 'tiktok');
  const tiktokSettingsComplete = selectedTikTokProfiles.every(profile => {
    const info = tiktokCreatorInfo[profile.id];
    const settings = tiktokSettings[profile.id];
    return info?.can_post !== false
      && Boolean(settings?.privacy_level)
      && ['allow_comment', 'allow_duet', 'allow_stitch'].every(key => typeof settings?.[key] === 'boolean')
      && settings?.music_usage_confirmed === true
      && settings?.upload_consent === true
      && (!settings?.commercial_content || settings?.brand_organic || settings?.brand_content)
      && !(settings?.brand_content && settings?.privacy_level === 'SELF_ONLY');
  });

  const buildPlatformSettings = () => selectedTikTokProfiles.reduce((result, profile) => {
    result[profile.id] = tiktokSettings[profile.id];
    return result;
  }, {});

  const updateTiktokSetting = (profileId, patch) => {
    setTiktokSettings(prev => ({
      ...prev,
      [profileId]: { ...(prev[profileId] || {}), ...patch },
    }));
  };

  const applyBestTimeSuggestion = () => {
    if (!scheduleDate || actionableSelectedProfiles.length === 0) {
      toast.warning(t('editor.selectDateAndSocials'));
      return;
    }
    if (customPerSocial) {
      const suggestedTimes = actionableSelectedProfiles.reduce((acc, profile) => {
        acc[profile.id] = getPlatformSuggestedTime(profile.platform, scheduleDate);
        return acc;
      }, {});
      setSocialTimes(prev => ({ ...prev, ...suggestedTimes }));
    } else {
      const unified = getUnifiedSuggestedTime(
        actionableSelectedProfiles.map(profile => profile.platform),
        scheduleDate
      );
      setScheduleTime(unified);
    }
    toast.success(t('editor.bestTimeApplied'));
  };

  const buildEditableContentPayload = () => {
    const payload = {
      script: content.format === 'post' ? '' : editScript,
      caption: editCaption,
      hashtags: editHashtags,
      opening_hook: editOpeningHook,
      visual_direction: editVisualDirection,
    };

    if (content.format === 'carousel') {
      const parsedSlides = parseCarouselSlidesFromEditableScript(editScript);
      payload.slides = parsedSlides.length > 0
        ? parsedSlides
        : (Array.isArray(content.slides) ? content.slides : []);
    }

    return payload;
  };

  const persistEditableContent = async () => {
    const payload = buildEditableContentPayload();
    await api.put(`/contents/${content.id}`, payload);
    const updated = { ...content, ...payload };
    setContent(updated);
    onUpdate?.(updated);
    return updated;
  };

  const save = async () => {
    setSaving(true);
    try {
      await persistEditableContent();
    } catch {}
    setSaving(false);
  };

  const openRegenerateModal = () => {
    setRegenerateError('');
    setShowRegenerateModal(true);
  };

  const closeRegenerateModal = () => {
    if (regenerating) return;
    setShowRegenerateModal(false);
    setRegenerateError('');
  };

  // Replaces the old window.confirm() with a real objective miniwizard
  // (CONTENT-REGEN-004). The backend reads the persisted content as source
  // material, so any unsaved manual edits must be flushed via
  // persistEditableContent() first — if that PUT fails, the AI regeneration
  // call must never run, and the modal stays open for another attempt.
  const confirmRegenerate = async (objective) => {
    if (regenerateLockRef.current) return;
    regenerateLockRef.current = true;
    setRegenerating(true);
    setRegenerateError('');
    try {
      await persistEditableContent();
    } catch (e) {
      setRegenerateError(e.response?.data?.detail || e.message);
      setRegenerating(false);
      regenerateLockRef.current = false;
      return;
    }
    try {
      const contentLanguage = String(project?.language || i18n.language || 'it').toLowerCase().split('-', 1)[0] || 'it';
      const { data } = await api.post('/contents/regenerate', {
        content_id: content.id,
        project_id: project.id,
        language: contentLanguage,
        objective,
      });
      setEditScript(data.script || ''); setEditCaption(data.caption || ''); setEditHashtags(String(data.hashtags || ''));
      setEditOpeningHook(data.opening_hook || ''); setEditVisualDirection(data.visual_direction || '');
      setContent(data); onUpdate?.(data);
      toast.success(t('editor.regenerateSuccess'));
      setShowRegenerateModal(false);
    } catch (e) {
      setRegenerateError(e.response?.data?.detail || e.message);
    }
    setRegenerating(false);
    regenerateLockRef.current = false;
  };

  const convert = async (targetOverride) => {
    const target = targetOverride || (content.format === 'reel' ? 'carousel' : 'reel');
    if (!window.confirm(t('editor.convertConfirm', { format: t(`format.${target}`) }))) return;
    setSaving(true);
    const tid = toast.loading(t('editor.convertLoading', { format: t(`format.${target}`) }));
    try {
      const contentLanguage = String(project?.language || i18n.language || 'it').toLowerCase().split('-', 1)[0] || 'it';
      const { data } = await api.post('/contents/convert', { content_id: content.id, project_id: project.id, target_format: target, language: contentLanguage });
      setEditScript(data.script || ''); setEditCaption(data.caption || ''); setEditHashtags(String(data.hashtags || ''));
      setEditOpeningHook(data.opening_hook || ''); setEditVisualDirection(data.visual_direction || '');
      setContent(data); onUpdate?.(data);
      toast.success(t('editor.convertSuccess', { format: t(`format.${target}`) }), { id: tid });
    } catch (e) { toast.error(`${t('common.error')}: ${e.response?.data?.detail || e.message}`, { id: tid }); }
    setSaving(false);
  };

  const publish = async () => {
    if (selectedSocials.length === 0) { toast.warning(t('editor.noSocials')); return; }
    if (!tiktokSettingsComplete) { toast.warning('Completa le impostazioni TikTok prima di pubblicare.'); return; }
    setPublishing(true);
    const tid = toast.loading(t('editor.publishing'));
    try {
      await save();
      const now = new Date().toISOString();
      const { data } = await api.post('/publish/schedule', { content_id: content.id, project_id: project.id, social_profile_ids: selectedSocials, scheduled_at: now, platform_settings: buildPlatformSettings() });
      const queuedItems = data?.items || [];
      setContentQueueItems(queuedItems);
      const updated = { ...content, status: 'scheduled' };
      setContent(updated); onUpdate?.(updated);
      setShowSchedule(false);
      setCustomPerSocial(false);
      toast.success(t('editor.publishStarted'), { id: tid });
      onClose?.();
    } catch (e) { toast.error(t('editor.publishError', { message: e.response?.data?.detail || e.message }), { id: tid }); }
    setPublishing(false);
  };

  const cancelSchedule = async () => {
    setCancellingSchedule(true);
    const tid = toast.loading(t('editor.cancellingSchedule'));
    try {
      if (contentQueueItems.length > 0) {
        await Promise.all(contentQueueItems.map(item => api.delete(`/publish/queue/${item.id}`).catch(() => {})));
      }
      setContentQueueItems([]);
      const updated = { ...content, status: 'draft' };
      setContent(updated); onUpdate?.(updated);
      toast.success(t('editor.cancelScheduleSuccess'), { id: tid });
    } catch (e) { toast.error(`${t('common.error')}: ${e.response?.data?.detail || e.message}`, { id: tid }); }
    setCancellingSchedule(false);
  };

  const schedule = async () => {
    if (!scheduleDate || selectedSocials.length === 0) { toast.warning(t('editor.selectDateAndSocials')); return; }
    if (!tiktokSettingsComplete) { toast.warning('Completa le impostazioni TikTok prima di programmare.'); return; }
    setScheduling(true);
    const tid = toast.loading(contentQueueItems.length > 0 ? t('editor.rescheduling') : t('editor.scheduling'));
    try {
      await save();
      if (contentQueueItems.length > 0) {
        await Promise.all(contentQueueItems.map(item => api.delete(`/publish/queue/${item.id}`).catch(() => {})));
      }
      // Treat input times as local timezone, convert to UTC for storage
      const toUtcIso = (date, time) => new Date(`${date}T${time}:00`).toISOString();
      const socialSchedules = selectedSocials.map(id => ({
        social_profile_id: id,
        scheduled_at: toUtcIso(scheduleDate, (customPerSocial ? socialTimes[id] : null) || scheduleTime),
      }));
      await api.post('/publish/schedule', {
        content_id: content.id,
        project_id: project.id,
        social_profile_ids: selectedSocials,
        scheduled_at: toUtcIso(scheduleDate, scheduleTime),
        social_schedules: socialSchedules,
        platform_settings: buildPlatformSettings(),
      });
      const { data: queueData } = await api.get(`/publish/queue/${project.id}`);
      const newItems = queueData.filter(q => q.content_id === content.id && q.status === 'queued');
      setContentQueueItems(newItems);
      const updated = { ...content, status: 'scheduled' };
      setContent(updated); onUpdate?.(updated);
      setShowSchedule(false);
      toast.success(t('editor.scheduleSuccess', { date: scheduleDate, time: scheduleTime }), { id: tid });
      onClose?.();
    } catch (e) { toast.error(t('editor.scheduleError', { message: e.response?.data?.detail || e.message }), { id: tid }); }
    setScheduling(false);
  };

  const markAsPublished = async () => {
    setMarkingPublished(true);
    const tid = toast.loading(t('editor.statusUpdating'));
    try {
      if (contentQueueItems.length > 0) {
        await Promise.all(
          contentQueueItems.map(item =>
            api.delete(`/publish/queue/${item.id}`).catch(() => {})
          )
        );
      }
      await api.post(`/publish/mark-published/${content.id}`);
      setContentQueueItems([]);
      const updated = { ...content, status: 'published' };
      setContent(updated);
      onUpdate?.(updated);
      toast.success(t('editor.statusUpdateSuccess'), { id: tid });
    } catch (e) {
      toast.error(t('editor.statusUpdateError', { message: e.response?.data?.detail || e.message }), { id: tid });
    }
    setMarkingPublished(false);
  };

  const uploadMedia = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    setUploadingMedia(true);
    const tid = toast.loading(t('editor.uploading'));
    try {
      const { data } = await api.post(`/media/upload/${content.id}`, fd);
      setContent(prev => {
        const updated = { ...prev, media: [...(prev.media || []), data] };
        onUpdate?.(updated);
        return updated;
      });
      toast.success(t('editor.fileUploaded'), { id: tid });
    } catch (e) { toast.error(t('editor.uploadError', { message: e.response?.data?.detail || e.message }), { id: tid }); }
    setUploadingMedia(false);
  };

  const uploadDroppedMedia = async (files) => {
    const mediaFiles = Array.from(files || []).filter(file =>
      file.type?.startsWith('image/') || file.type?.startsWith('video/')
    );
    if (mediaFiles.length === 0) {
      toast.warning(t('editor.uploadMedia'));
      return;
    }
    for (const file of mediaFiles) {
      await uploadMedia(file);
    }
  };

  const syncCarouselReferenceAssets = (assets) => {
    setCarouselReferenceAssets(assets);
    setContent(prev => {
      const updated = { ...prev, carousel_reference_assets: assets };
      onUpdate?.(updated);
      return updated;
    });
  };

  const uploadCarouselReferenceAsset = async (file, slidePosition) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post(
      `/media/carousel-reference/${content.id}?slide_position=${encodeURIComponent(slidePosition)}`,
      fd
    );
    setCarouselReferenceAssets(prev => {
      const updatedAssets = [...prev, data];
      setContent(current => {
        const updated = { ...current, carousel_reference_assets: updatedAssets };
        onUpdate?.(updated);
        return updated;
      });
      return updatedAssets;
    });
  };

  const uploadCarouselReferenceAssets = async (files) => {
    const imageFiles = Array.from(files || []).filter(file => file.type?.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setUploadingCarouselReference(true);
    const tid = toast.loading(t('editor.carouselReferenceUploading'));
    try {
      for (let index = 0; index < imageFiles.length; index += 1) {
        const defaultPosition = carouselReferenceAssets.length === 0 && index === 0 ? '1' : 'last';
        await uploadCarouselReferenceAsset(imageFiles[index], defaultPosition);
      }
      toast.success(t('editor.carouselReferenceUploadSuccess'), { id: tid });
    } catch (e) {
      toast.error(t('editor.carouselReferenceUploadError', { message: e.response?.data?.detail || e.message }), { id: tid });
    }
    setUploadingCarouselReference(false);
  };

  const updateCarouselReferencePosition = async (assetId, slidePosition) => {
    try {
      const { data } = await api.patch(`/media/carousel-reference/${content.id}/${assetId}`, { slide_position: slidePosition });
      syncCarouselReferenceAssets(carouselReferenceAssets.map(asset => asset.id === assetId ? data : asset));
    } catch (e) {
      toast.error(t('editor.carouselReferenceUpdateError'));
    }
  };

  const deleteCarouselReferenceAsset = async (assetId) => {
    try {
      await api.delete(`/media/carousel-reference/${content.id}/${assetId}`);
      syncCarouselReferenceAssets(carouselReferenceAssets.filter(asset => asset.id !== assetId));
    } catch (e) {
      toast.error(t('editor.carouselReferenceDeleteError'));
    }
  };

  const deleteMedia = async (mediaId) => {
    setDeletingMediaId(mediaId);
    try {
      await api.delete(`/media/${content.id}/${mediaId}`);
      const updated = { ...content, media: (content.media || []).filter(m => m.id !== mediaId) };
      setContent(updated); onUpdate?.(updated);
      toast.success(t('editor.fileDeleted'));
    } catch (e) { toast.error(t('editor.deleteError')); }
    setDeletingMediaId(null);
  };

  const fetchWhatsappStatusFiles = async () => {
    if (whatsappStatusMedia.length === 0) {
      throw new Error(t('editor.whatsappStatusNoSlides'));
    }
    const files = [];
    for (let index = 0; index < whatsappStatusMedia.length; index += 1) {
      const media = whatsappStatusMedia[index];
      const { data } = await api.get(
        `/media/whatsapp-status/${content.id}/${media.id}`,
        { responseType: 'blob' }
      );
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'image/jpeg' });
      files.push(new File(
        [blob],
        `sketchario-status-${String(index + 1).padStart(2, '0')}.jpg`,
        { type: 'image/jpeg' }
      ));
    }
    return files;
  };

  const shareWhatsappStatus = async () => {
    if (whatsappStatusMedia.length === 0) {
      toast.warning(t('editor.whatsappStatusNoSlides'));
      return;
    }
    setWhatsappStatusBusy(true);
    const tid = toast.loading(t('editor.whatsappStatusPreparing'));
    try {
      const files = await fetchWhatsappStatusFiles();
      const sharePayload = { files, title: t('editor.whatsappStatusShareTitle') };
      const supportsShare = typeof navigator.share === 'function'
        && (typeof navigator.canShare !== 'function' || navigator.canShare(sharePayload));
      if (!supportsShare) {
        toast.warning(t('editor.whatsappStatusNativeUnavailable'), { id: tid });
        return;
      }
      await navigator.share(sharePayload);
      toast.success(t('editor.whatsappStatusShared'), { id: tid });
    } catch (error) {
      if (error?.name === 'AbortError') {
        toast.dismiss(tid);
      } else {
        toast.error(t('editor.whatsappStatusError', { message: error.response?.data?.detail || error.message }), { id: tid });
      }
    } finally {
      setWhatsappStatusBusy(false);
    }
  };

  const downloadWhatsappStatusPack = async () => {
    if (whatsappStatusMedia.length === 0) {
      toast.warning(t('editor.whatsappStatusNoSlides'));
      return;
    }
    setWhatsappStatusBusy(true);
    const tid = toast.loading(t('editor.whatsappStatusPreparing'));
    try {
      const { data } = await api.get(
        `/media/whatsapp-status-pack/${content.id}`,
        { responseType: 'blob' }
      );
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'sketchario-whatsapp-status.zip';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success(t('editor.whatsappStatusDownloaded'), { id: tid });
    } catch (error) {
      toast.error(t('editor.whatsappStatusError', { message: error.response?.data?.detail || error.message }), { id: tid });
    } finally {
      setWhatsappStatusBusy(false);
    }
  };

  const hashtagList = String(editHashtags || '').split(/[\s,]+/).filter(h => h.length > 1).map(h => h.startsWith('#') ? h : `#${h}`);

  /* Shared components */
  const SocialColumn = () => (
    <div className={isMobile ? 'p-4' : 'w-52 border-r border-[var(--border-color)] p-4 overflow-y-auto flex-shrink-0'}>
      <p className="text-xs font-semibold text-[var(--text-muted)] uppercase mb-3">{t('project.social.publishOn')}</p>
      {socialProfiles.map(prof => {
        const pi = PLATFORM_ICONS[prof.platform] || { Icon: Globe, color: '#fff', name: prof.platform };
        const isPublished = publishedSocials.includes(prof.id);
        const isSelected = selectedSocials.includes(prof.id);
        const isVisible = isPublished || isSelected;
        const isRepublishSelected = isPublished && isSelected;
        const isDisabled = socialsLocked;
        return (
          <div key={prof.id} data-testid={`social-toggle-${prof.platform}`}
            className="flex items-center gap-2 p-2 rounded-lg mb-2 transition-all"
            style={{
              background: isVisible ? (isRepublishSelected ? `${pi.color}18` : isPublished ? 'rgba(255,255,255,0.06)' : socialsLocked ? 'rgba(255,255,255,0.06)' : `${pi.color}15`) : 'transparent',
              border: isVisible ? `1.5px solid ${isRepublishSelected ? pi.color : isPublished ? 'rgba(156,163,175,0.45)' : socialsLocked ? 'rgba(255,255,255,0.12)' : pi.color}` : '1.5px solid transparent',
              opacity: isRepublishSelected ? 1 : isPublished ? 0.65 : socialsLocked ? (isSelected ? 0.7 : 0.35) : (isSelected ? 1 : 0.5),
              cursor: isDisabled ? 'default' : 'pointer'
            }}
            title={isPublished ? t('editor.socialAlreadyPublished') : socialsLocked ? t('editor.socialSelectionLocked') : pi.name}
            onClick={() => { if (!isDisabled) toggleSocial(prof.id); }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${pi.color}20` }}>
              <pi.Icon weight="fill" size={16} color={pi.color} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{prof.profile_name}</p>
              <p className="text-[10px] text-[var(--text-muted)]">{pi.name}</p>
            </div>
            {prof.platform === 'tiktok' && isSelected && (
              <button
                type="button"
                aria-label="Impostazioni TikTok"
                data-testid={`tiktok-settings-toggle-${prof.id}`}
                className="p-1 rounded-md hover:bg-white/10"
                onClick={event => {
                  event.stopPropagation();
                  setOpenTiktokSettings(prev => ({ ...prev, [prof.id]: !prev[prof.id] }));
                }}
              >
                <GearSix size={14} color="#69C9D0" />
              </button>
            )}
            {isVisible && <Check size={14} weight="bold" color={isRepublishSelected ? pi.color : isPublished || socialsLocked ? '#9ca3af' : pi.color} />}
          </div>
        );
      })}
      {selectedTikTokProfiles.map(profile => {
        if (!openTiktokSettings[profile.id]) return null;
        const info = tiktokCreatorInfo[profile.id];
        const settings = tiktokSettings[profile.id] || {};
        const optionLabels = {
          PUBLIC_TO_EVERYONE: 'Tutti',
          MUTUAL_FOLLOW_FRIENDS: 'Amici reciproci',
          FOLLOWER_OF_CREATOR: 'Follower',
          SELF_ONLY: 'Solo io',
        };
        if (loadingTiktokInfo[profile.id] || !info) {
          return <p key={profile.id} className="text-[10px] text-[var(--text-muted)] px-2 py-3">Caricamento impostazioni TikTok…</p>;
        }
        return (
          <div key={profile.id} data-testid={`tiktok-settings-panel-${profile.id}`} className="mb-3 p-3 rounded-xl space-y-3" style={{ background: 'rgba(37,244,238,0.06)', border: '1px solid rgba(37,244,238,0.2)' }}>
            <div>
              <p className="text-xs font-semibold text-white">{info.nickname || profile.profile_name}</p>
              <p className="text-[10px] text-[var(--text-muted)]">Impostazioni di questo contenuto</p>
            </div>
            {info.can_post === false && <p className="text-[10px] text-red-400">Questo account non può pubblicare ora.</p>}
            <label className="block text-[10px] text-[var(--text-muted)]">
              Privacy
              <select
                aria-label="Privacy TikTok"
                value={settings.privacy_level || ''}
                onChange={event => updateTiktokSetting(profile.id, { privacy_level: event.target.value })}
                className="input-dark w-full mt-1 text-xs"
              >
                <option value="">Seleziona…</option>
                {(info.privacy_level_options || []).map(option => <option key={option} value={option}>{optionLabels[option] || option}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-1 gap-2 text-[11px]">
              {[
                ['allow_comment', 'Commenti', info.comment_disabled],
                ['allow_duet', 'Duet', info.duet_disabled],
                ['allow_stitch', 'Stitch', info.stitch_disabled],
              ].map(([key, label, disabled]) => (
                <label key={key} className="flex items-center gap-2">
                  <input type="checkbox" checked={Boolean(settings[key])} disabled={disabled} onChange={event => updateTiktokSetting(profile.id, { [key]: event.target.checked })} />
                  <span className={disabled ? 'text-[var(--text-muted)]' : ''}>{label}{disabled ? ' · non disponibile' : ''}</span>
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-[11px]">
              <input type="checkbox" checked={Boolean(settings.commercial_content)} onChange={event => updateTiktokSetting(profile.id, { commercial_content: event.target.checked, ...(!event.target.checked ? { brand_organic: false, brand_content: false } : {}) })} />
              Contenuto commerciale
            </label>
            {settings.commercial_content && (
              <div className="pl-5 space-y-2 text-[11px]">
                <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(settings.brand_organic)} onChange={event => updateTiktokSetting(profile.id, { brand_organic: event.target.checked })} />Il tuo brand</label>
                <label className="flex items-center gap-2"><input type="checkbox" disabled={settings.privacy_level === 'SELF_ONLY'} checked={Boolean(settings.brand_content)} onChange={event => updateTiktokSetting(profile.id, { brand_content: event.target.checked })} />Brand partner</label>
              </div>
            )}
            <label className="flex items-center gap-2 text-[11px]"><input type="checkbox" checked={Boolean(settings.is_aigc)} onChange={event => updateTiktokSetting(profile.id, { is_aigc: event.target.checked })} />Contenuto generato con AI</label>
            <label className="flex items-start gap-2 text-[10px]"><input className="mt-0.5" type="checkbox" checked={Boolean(settings.music_usage_confirmed)} onChange={event => updateTiktokSetting(profile.id, { music_usage_confirmed: event.target.checked })} /><span>Confermo di rispettare le regole TikTok sull’utilizzo della musica.</span></label>
            <label className="flex items-start gap-2 text-[10px]"><input className="mt-0.5" type="checkbox" checked={Boolean(settings.upload_consent)} onChange={event => updateTiktokSetting(profile.id, { upload_consent: event.target.checked })} /><span>Autorizzo l’invio di questo contenuto al profilo TikTok indicato.</span></label>
          </div>
        );
      })}
      {socialProfiles.length === 0 && <p className="text-xs text-[var(--text-muted)]">{t('project.social.noSocialConnected')}</p>}
      {publishedSocials.length > 0 && (
        <p className="text-[10px] text-[var(--text-muted)] mt-2">
          {t('editor.socialAlreadyPublished')}
        </p>
      )}
      {socialsLocked && selectedSocials.length > 0 && (
        <p className="text-[10px] text-[var(--text-muted)] mt-2">
          {t('editor.socialSelectionLocked')}
        </p>
      )}
    </div>
  );

  const EditorColumn = () => (
    <div className={isMobile ? 'p-4' : 'flex-1 overflow-y-auto p-6'}>
      {content.format === 'prompted_reel' && (
        <>
          <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)' }}>
            <p className="text-xs font-semibold mb-2" style={{ color: '#a855f7' }}>{`⚡ ${t('editor.openingHook').toUpperCase()}`}</p>
            <textarea className="input-dark w-full text-sm" rows={2} value={editOpeningHook} onChange={e => setEditOpeningHook(e.target.value)}
              placeholder={t('editor.openingHookPlaceholder')} style={{ paddingLeft: '1rem', lineHeight: 1.7 }} />
          </div>
          <div className="mb-4">
            <p className="text-xs font-semibold text-[var(--text-muted)] uppercase mb-2">{t('editor.avatarScript')}</p>
            <textarea className="input-dark w-full text-sm" rows={isMobile ? 6 : 8} value={editScript} onChange={e => setEditScript(e.target.value)}
              placeholder={t('editor.avatarScriptPlaceholder')} style={{ paddingLeft: '1rem', lineHeight: 1.7 }} />
          </div>
          <div className="mb-4">
            <p className="text-xs font-semibold text-[var(--text-muted)] uppercase mb-2">{t('editor.visualDirection')}</p>
            <textarea className="input-dark w-full text-sm" rows={isMobile ? 3 : 4} value={editVisualDirection} onChange={e => setEditVisualDirection(e.target.value)}
              placeholder={t('editor.visualDirectionPlaceholder')} style={{ paddingLeft: '1rem', lineHeight: 1.7 }} />
          </div>
        </>
      )}
      {content.format === 'post' && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-[var(--text-muted)] uppercase mb-2">{t('editor.visualDirection')}</p>
          <textarea className="input-dark w-full text-sm" rows={isMobile ? 3 : 4} value={editVisualDirection} onChange={e => setEditVisualDirection(e.target.value)}
            placeholder={t('editor.visualDirectionPlaceholder')} style={{ paddingLeft: '1rem', lineHeight: 1.7 }} />
        </div>
      )}
      {content.format !== 'prompted_reel' && content.format !== 'post' && (
        <div className="mb-4">
          <textarea className="input-dark w-full text-sm" rows={isMobile ? 4 : 6} value={editScript} onChange={e => setEditScript(e.target.value)}
            placeholder={t('editor.scriptPlaceholder')} style={{ paddingLeft: '1rem', lineHeight: 1.7 }} />
        </div>
      )}
      {carouselCopyEditor}
      <div className="mb-4">
        <p className="text-xs font-semibold text-[var(--text-muted)] uppercase mb-2">{t('editor.caption')}</p>
        <RichCaption value={editCaption} onChange={setEditCaption} />
      </div>
      <div className="mb-4">
        <p className="text-xs font-semibold text-[var(--text-muted)] uppercase mb-2">{t('editor.hashtags')}</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {hashtagList.map((h, i) => (
            <span key={i} className="px-2 py-0.5 rounded-md text-xs font-medium" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>{h}</span>
          ))}
        </div>
        <input className="input-dark w-full text-sm" value={editHashtags} onChange={e => setEditHashtags(e.target.value)}
          placeholder={t('editor.hashtagsPlaceholder')} style={{ paddingLeft: '1rem' }} />
      </div>
      <div className="mb-4">
        <p className="text-xs font-semibold text-[var(--text-muted)] uppercase mb-2">{t('editor.media')}</p>
        <label
          className="block p-4 md:p-6 rounded-lg border border-dashed border-[var(--border-color)] text-center cursor-pointer hover:border-[var(--gradient-start)] transition-colors mb-3"
          onDragOver={e => {
            if (uploadingMedia) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={async e => {
            e.preventDefault();
            if (uploadingMedia) return;
            await uploadDroppedMedia(e.dataTransfer.files);
          }}
          style={uploadingMedia ? { opacity: 0.6, pointerEvents: 'none' } : {}}
        >
          {uploadingMedia ? (
            <>
              <div className="w-5 h-5 border-2 border-[var(--gradient-start)] border-t-transparent rounded-full animate-spin mx-auto mb-1" />
              <p className="font-medium text-sm text-[var(--accent-purple)]">{t('editor.uploading')}</p>
            </>
          ) : (
            <>
              <p className="font-medium text-sm">{t('editor.uploadMedia')}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">{t('editor.uploadMax')}</p>
            </>
          )}
          <input type="file" accept="image/*,video/*" multiple className="hidden" disabled={uploadingMedia} onChange={async e => { const files = Array.from(e.target.files); e.target.value = ''; await uploadDroppedMedia(files); }} />
        </label>
        {generatingImage && (
          <div className="flex items-center gap-3 p-3 rounded-lg mb-3" style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)' }}>
            <div className="w-4 h-4 border-2 border-[var(--accent-purple)] border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <p className="text-xs text-[var(--accent-purple)]">{t('editor.imageGenerating')}</p>
          </div>
        )}
        {content.media && content.media.length > 0 && (() => {
          const imgMedia = content.media.filter(m => m.type === 'image');
          return (
          <div className="flex gap-2 flex-wrap mb-3">
            {content.media.map(m => (
              (() => {
                const logoState = getGeneratedMediaLogoState(m);
                const logoLabel = logoState === 'applied'
                  ? t('editor.logoApplied')
                  : logoState === 'failed'
                    ? t('editor.logoFailed')
                    : '';
                return (
              <div
                key={m.id}
                className={`relative group w-14 h-14 md:w-16 md:h-16 ${dragMediaId === m.id ? 'opacity-60 scale-95' : ''}`}
                draggable
                onDragStart={() => setDragMediaId(m.id)}
                onDragEnd={() => setDragMediaId(null)}
                onDragOver={e => e.preventDefault()}
                onDrop={async e => {
                  e.preventDefault();
                  const sourceId = dragMediaId;
                  setDragMediaId(null);
                  await reorderContentMedia(sourceId, m.id);
                }}
                title={m.type === 'image' ? 'Trascina per riordinare le slide' : 'Trascina per riordinare'}
                style={{ cursor: 'grab' }}
              >
                {m.type === 'image' ? (
                  <img src={resolveAssetUrl(m.url)} alt="" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <div className="relative w-full h-full rounded-lg overflow-hidden bg-[var(--bg-secondary)]">
                    <video
                      src={resolveAssetUrl(m.url)}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                      <Video size={18} color="white" weight="fill" />
                    </div>
                  </div>
                )}
                <div
                  className="absolute left-1 top-1 rounded px-1 py-0.5 text-[9px] font-semibold pointer-events-none"
                  style={{ background: 'rgba(0,0,0,0.55)', color: 'white' }}
                >
                  {content.media.findIndex(item => item.id === m.id) + 1}
                </div>
                {logoState && (
                  <div
                    className="absolute right-1 top-1 rounded px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.08em] pointer-events-none"
                    title={logoState === 'failed' ? `${logoLabel}: ${m.brand_logo_error}` : logoLabel}
                    style={{
                      background: logoState === 'applied' ? 'rgba(16,185,129,0.9)' : 'rgba(244,114,182,0.95)',
                      color: 'white',
                    }}
                  >
                    {logoLabel}
                  </div>
                )}
                {m.type === 'image' && (
                  <button className="absolute inset-0 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setLightboxIdx(imgMedia.findIndex(im => im.id === m.id))}>
                    <Eye size={18} color="white" />
                    {imgMedia.length > 1 && <span className="absolute bottom-1 right-1 text-[9px] font-bold bg-black/60 rounded px-1">{imgMedia.findIndex(im => im.id === m.id) + 1}/{imgMedia.length}</span>}
                  </button>
                )}
                <button className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[var(--accent-pink)] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => deleteMedia(m.id)} disabled={deletingMediaId === m.id}>
                  {deletingMediaId === m.id
                    ? <div className="w-2 h-2 border border-white border-t-transparent rounded-full animate-spin" />
                    : <X size={8} color="white" />}
                </button>
              </div>
                );
              })()
            ))}
          </div>
          );
        })()}
      </div>
      <div className="content-action-toolbar">
        {content.format === 'prompted_reel' ? (
          <button className="btn-gradient content-action-copy" onClick={() => {
            const avatarScript = `OPENING:\n${editOpeningHook}\n\nSCRIPT:\n${editScript}\n\nREGIA:\n${editVisualDirection}`;
            navigator.clipboard.writeText(avatarScript); toast.success(t('editor.avatarScriptCopied'));
          }}>
            🤖 {t('editor.copyAvatarScript')}
          </button>
        ) : (
          <button className="btn-ghost content-action-copy" onClick={() => {
            const copyParts = content.format === 'post'
              ? [richTextToPlainText(editCaption), editHashtags]
              : [editScript, richTextToPlainText(editCaption), editHashtags];
            navigator.clipboard.writeText(copyParts.filter(Boolean).join('\n\n'));
            toast.success(t('common.copied'));
          }}>
            <Copy size={14} /> {t('common.copy')}
          </button>
        )}

        <div className="content-action-group content-action-group--convert">
          <span className="content-action-group-label">{t('editor.formatTools')}</span>
          <div className="content-action-buttons">
            {content.format === 'prompted_reel' ? (
              <>
                <button className="content-action-button" onClick={() => convert('reel')}><Video size={15} /> {t('format.reel')}</button>
                <button className="content-action-button" onClick={() => convert('carousel')}><Image size={15} /> {t('format.carousel')}</button>
              </>
            ) : (
              <>
                <button className="content-action-button" onClick={() => convert()}>
                  {content.format === 'reel' ? <><Image size={15} /> {t('format.carousel')}</> : <><Video size={15} /> {t('format.reel')}</>}
                </button>
                <button className="content-action-button" onClick={() => convert('prompted_reel')}>
                  <span aria-hidden="true">🤖</span> {t('format.prompted_reel')}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="content-action-group content-action-group--create">
          <span className="content-action-group-label">{t('editor.creationTools')}</span>
          <div className="content-action-buttons">
            <button className="content-action-button" onClick={openRegenerateModal} disabled={saving || regenerating} data-testid="content-regenerate-btn">
              <ArrowClockwise size={15} /> {t('editor.regenerate')}
            </button>
            {content.format === 'carousel' ? (
              !hasPremiumVisualRender && carouselSlides.length > 0 && (
                <button
                  className="content-action-button"
                  type="button"
                  data-testid="carousel-ai-open-btn"
                  onClick={() => {
                    setAiCarouselMode('text_images');
                    setShowAiCarouselStudio(true);
                  }}
                >
                  <Sparkle size={15} weight="fill" /> {t('editor.generateCarouselAi')}
                </button>
              )
            ) : (
              <button className="content-action-button" onClick={() => {
                const defaultImagePrompt = content.format === 'post'
                  ? (editVisualDirection || content.hook_text || richTextToPlainText(editCaption) || '')
                  : (editVisualDirection || editScript || '');
                setInputModal({ title: t('editor.imagePromptTitle'), placeholder: t('editor.imagePromptPlaceholder'), value: defaultImagePrompt, multiline: true, isImagePrompt: true,
                  onConfirm: async (prompt) => {
                    setGeneratingImage(true);
                    try { const { data } = await api.post('/media/generate-dalle', { content_id: content.id, prompt, project_id: project.id, model: enforcedVisualModel }); const updated = { ...content, media: [...(content.media||[]), data] }; setContent(updated); onUpdate?.(updated); toast.success(t('editor.imageGenerated')); } catch(e) { toast.error(t('editor.imageGenerationError', { message: e.response?.data?.detail || e.message })); }
                    setGeneratingImage(false);
                  },
                });
              }}>
                <Sparkle size={15} weight="fill" /> {t('editor.generateImageShort')}
              </button>
            )}
            {content.format === 'carousel' && carouselSlides.length > 0 && hasPremiumVisualRender && (
              <button className="content-action-button" onClick={() => {
                setCarouselSlidesCount(carouselSlides.length);
                setShowCarouselStudio(true);
              }}>
                <Images size={15} /> {t('editor.generateCarouselSlides')}
              </button>
            )}
            <button
              className="content-action-button relative"
              onClick={handleCanvaClick}
              disabled={canvaLoading}
            >
              {canvaLoading
                ? <div className="w-4 h-4 border-2 border-[#7D2AE8] border-t-transparent rounded-full animate-spin" />
                : <CanvaIcon size={15} />}
              {t('editor.openCanvaShort')}
              {canvaConnected && <span className="content-action-status-dot" />}
            </button>
            {HEYGEN_FEATURE_ENABLED && ['prompted_reel', 'reel'].includes(content.format) && (
              <button
                className="content-action-button content-action-button--heygen"
                onClick={handleHeyGenClick}
                disabled={heyGenBusy}
                title={t('editor.heygenHint')}
              >
                {heyGenBusy
                  ? <div className="w-4 h-4 border-2 border-cyan-300 border-t-transparent rounded-full animate-spin" />
                  : <Video size={15} weight="fill" />}
                {heyGenConnector?.status === 'connected' ? t('editor.heygenCreate') : t('editor.heygenConnect')}
                {heyGenConnector?.status === 'connected' && <span className="content-action-status-dot" />}
              </button>
            )}
          </div>
        </div>

        <div className="content-action-group content-action-group--source">
          <span className="content-action-group-label">{t('editor.mediaSources')}</span>
          <div className="content-action-buttons">
            <button className="content-action-button" onClick={openDrivePicker}>
              <Download size={15} weight="bold" /> {t('editor.googleDrive')}
            </button>
            <button className="content-action-button" onClick={openLibrary}>
              <Images size={15} /> {t('editor.mediaLibraryShort')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const PreviewColumn = () => (
    <div className={isMobile ? 'p-4' : 'w-80 border-l border-[var(--border-color)] p-4 overflow-y-auto flex-shrink-0'}>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-semibold text-[var(--text-muted)] uppercase">{t('editor.preview')}</p>
        {displayedSocialIds.length > 0 && <span className="badge blue text-[10px]">{displayedSocialIds.length} {t('editor.socials')}</span>}
      </div>
      {selectedProfiles.length > 0 ? selectedProfiles.map(prof => (
        <div key={prof.id} className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            {(() => { const pi = PLATFORM_ICONS[prof.platform] || { Icon: Globe, color: '#888', name: prof.platform }; return <><pi.Icon size={12} color={pi.color} weight="fill" /><span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">{pi.name}</span></>; })()}
          </div>
          <SocialMockup
            prof={prof}
            format={content.format}
            caption={editCaption}
            hashtags={editHashtags}
            media={content.media}
            backendUrl={process.env.REACT_APP_BACKEND_URL}
            t={t}
          />
        </div>
      )) : (
        <div className="text-center py-8 md:py-12">
          <Eye size={28} className="mx-auto mb-3 text-[var(--text-muted)] opacity-30" />
          <p className="text-xs text-[var(--text-muted)] mb-1">{t('editor.selectSocials')}</p>
        </div>
      )}
    </div>
  );

  return (
    <>
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', padding: isMobile ? 0 : 24 }}
      data-testid="content-detail-overlay">
    <motion.div initial={{ scale: isMobile ? 1 : 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: isMobile ? 1 : 0.95, opacity: 0 }}
      className={`${isMobile ? 'w-full h-full' : 'w-full max-w-[1400px] h-[85vh] rounded-xl'} overflow-hidden flex flex-col`}
      style={{ background: 'var(--bg-primary)', border: isMobile ? 'none' : '1px solid var(--border-color)' }}
      onClick={e => e.stopPropagation()}>

      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-[var(--border-color)] flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`badge text-[10px] ${content.format === 'reel' ? 'pink' : content.format === 'prompted_reel' ? 'purple' : 'blue'}`}>
            {content.format === 'reel' ? <Video size={10} /> : content.format === 'prompted_reel' ? <span>🤖</span> : <Image size={10} />}
            <span className="ml-1">{content.format === 'prompted_reel' ? t('format.prompted_reel') : t(`format.${content.format}`)}</span>
          </span>
          {isAdminCarouselTemplateActive && (
            <span
              className="badge text-[10px]"
              title={t('editor.adminCarouselTemplateHint')}
              style={{ background: 'rgba(199,154,62,0.16)', color: '#f4d28b', border: '1px solid rgba(199,154,62,0.35)' }}
            >
              <Sparkle size={10} />
              <span className="ml-1">{t('editor.adminCarouselTemplateActive')}</span>
            </span>
          )}
          {content.format === 'carousel' && hasPremiumVisualRender && projectDesignDnaActive && !designDocumentCarouselRenderEnabled && (
            <span
              className="badge text-[10px]"
              title={t('editor.projectDesignDnaHint')}
              style={{ background: 'rgba(34,197,94,0.14)', color: 'var(--accent-green)', border: '1px solid rgba(34,197,94,0.28)' }}
            >
              <Sparkle size={10} />
              <span className="ml-1">{t('editor.projectDesignDnaActive')}</span>
            </span>
          )}
          <h2 className="font-semibold text-xs md:text-sm truncate" data-testid="content-hook-header">{content.hook_text}</h2>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`badge text-[10px] ${content.status === 'published' ? 'green' : content.status === 'scheduled' ? 'orange' : 'purple'}`}>
            {content.status ? t(`status.${content.status}`) : t('status.draft')}
          </span>
          <button onClick={onClose} className="btn-ghost p-1.5" data-testid="close-content-detail"><X size={18} /></button>
        </div>
      </div>

      {/* Mobile Tabs */}
      {isMobile && (
        <div className="flex border-b border-[var(--border-color)] flex-shrink-0">
          {[
            { id: 'editor', label: t('editor.editorTab') },
            { id: 'social', label: `${t('editor.socialTab')}${displayedSocialIds.length > 0 ? ` (${displayedSocialIds.length})` : ''}` },
            { id: 'preview', label: t('editor.previewTab') },
          ].map(t => (
            <button key={t.id} className={`flex-1 text-xs font-medium py-2.5 transition-colors ${mobileTab === t.id ? 'text-white border-b-2 border-[var(--gradient-start)]' : 'text-[var(--text-muted)]'}`}
              onClick={() => setMobileTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Content - Desktop: 3 cols, Mobile: tabbed single column */}
      <div className="flex flex-1 overflow-hidden">
        {isMobile ? (
          <div className="flex-1 overflow-y-auto">
            {mobileTab === 'social' && SocialColumn()}
            {mobileTab === 'editor' && EditorColumn()}
            {mobileTab === 'preview' && PreviewColumn()}
          </div>
        ) : (
          <>
            {SocialColumn()}
            {EditorColumn()}
            {PreviewColumn()}
          </>
        )}
      </div>

      {/* Bottom Bar */}
      <div className={`flex items-center justify-between px-4 md:px-6 py-2.5 md:py-3 border-t border-[var(--border-color)] flex-shrink-0 ${isMobile ? 'flex-wrap gap-2' : ''}`} style={{ background: 'var(--bg-secondary)' }}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-semibold ${content.status === 'published' ? 'text-[var(--accent-green)]' : content.status === 'scheduled' ? 'text-[var(--accent-orange)]' : 'text-[var(--accent-purple)]'}`}>
            {content.status === 'published' ? t('editor.published') : content.status === 'scheduled' ? t('editor.scheduled') : t('editor.draft')}
          </span>
          {content.status === 'scheduled' && contentQueueItems[0]?.scheduled_at && (
            <span className="text-[10px] font-medium" style={{ color: 'var(--accent-orange)' }}>
              {new Date(contentQueueItems[0].scheduled_at).toLocaleString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {publishedQueueItems.filter(item => item.post_url).slice(0, 4).map(item => {
            const pi = PLATFORM_ICONS[item.platform] || { Icon: Globe, color: '#888', name: item.platform };
            return (
              <a
                key={item.id}
                href={item.post_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold"
                style={{ background: 'rgba(59,130,246,0.12)', color: 'var(--accent-blue)', border: '1px solid rgba(59,130,246,0.24)' }}
                title={t('editor.openPublishedPost', { platform: pi.name })}
              >
                <pi.Icon size={11} weight="fill" color={pi.color} />
                <ArrowSquareOut size={10} />
                {pi.name}
              </a>
            );
          })}
          {displayedSocialIds.length > 0 && <span className="text-[10px] text-[var(--text-muted)]">{displayedSocialIds.length} {t('editor.socials')}</span>}
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button className="btn-ghost text-xs py-1.5" onClick={save} disabled={saving} data-testid="save-draft-btn">
            <FloppyDisk size={14} /> {saving ? '...' : t('common.save')}
          </button>
          {content.status !== 'published' && (
            <button className="btn-ghost text-xs py-1.5" onClick={markAsPublished} disabled={markingPublished}
              style={{ color: 'var(--accent-green)' }} title={t('editor.markAsPublishedTitle')}>
              {markingPublished
                ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                : <><CheckCircle size={14} /> {t('editor.markAsPublished')}</>}
            </button>
          )}
          <button className="btn-ghost text-xs py-1.5" onClick={publish} disabled={publishing || selectedSocials.length === 0 || !tiktokSettingsComplete} data-testid="publish-btn"
            style={selectedSocials.length === 0 || !tiktokSettingsComplete ? { opacity: 0.4 } : {}}>
            {publishing ? <><span className="animate-spin inline-block">⏳</span> {t('editor.publishing_')}</> : <><PaperPlaneTilt size={14} /> {t('editor.publish')}</>}
          </button>
          {content.format === 'carousel' && (
            <button
              className="btn-ghost text-xs py-1.5"
              type="button"
              data-testid="whatsapp-status-btn"
              onClick={() => setShowWhatsappStatus(true)}
              style={{ color: '#25D366' }}
              title={t('editor.whatsappStatusHint')}
            >
              <WhatsappLogo size={15} weight="fill" /> {t('editor.whatsappStatus')}
            </button>
          )}
          {(hasActiveSchedule || hasScheduledStatusOnly) && (
            <button className="btn-ghost text-xs py-1.5" onClick={cancelSchedule} disabled={cancellingSchedule}
              style={{ color: 'var(--accent-pink)' }} title={t('editor.cancelScheduleTitle')}>
              {cancellingSchedule
                ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                : <><XCircle size={14} /> {t('editor.cancelSchedule')}</>}
            </button>
          )}
          <button className="btn-gradient text-xs py-1.5" onClick={() => setShowSchedule(!showSchedule)} data-testid="schedule-btn"
            disabled={scheduling} style={{ opacity: scheduling ? 0.5 : 1 }}>
            <CalendarBlank size={14} /> {contentQueueItems.length > 0 ? t('editor.modifySchedule') : t('editor.scheduleBtn')}
          </button>
        </div>
      </div>

      {/* Schedule Popup — handled as fixed overlay below */}
    </motion.div>
    </motion.div>

    {showWhatsappStatus && (
      <div
        className="fixed inset-0 z-[250] flex items-end sm:items-center justify-center p-0 sm:p-4"
        style={{ background: 'rgba(0,0,0,0.76)', backdropFilter: 'blur(6px)' }}
        onMouseDown={event => {
          if (event.target === event.currentTarget && !whatsappStatusBusy) setShowWhatsappStatus(false);
        }}
        data-testid="whatsapp-status-modal"
      >
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden"
          style={{ background: 'var(--bg-primary)', border: '1px solid rgba(37,211,102,0.28)' }}
          onMouseDown={event => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-[var(--border-color)]">
            <div>
              <div className="flex items-center gap-2">
                <WhatsappLogo size={19} weight="fill" color="#25D366" />
                <h3 className="font-semibold">{t('editor.whatsappStatusTitle')}</h3>
                <ContextHelpButton articleId="whatsapp-status" label={t('editor.whatsappStatusHelp')} />
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                {t('editor.whatsappStatusDescription', { count: whatsappStatusMedia.length })}
              </p>
            </div>
            <button className="btn-ghost p-2" onClick={() => setShowWhatsappStatus(false)} disabled={whatsappStatusBusy}>
              <X size={16} />
            </button>
          </div>

          <div className="p-5 space-y-3">
            {whatsappStatusMedia.length === 0 ? (
              <div className="rounded-xl p-4 text-sm text-[var(--text-muted)]" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                {t('editor.whatsappStatusNoSlides')}
              </div>
            ) : (
              <>
                <div className="rounded-xl p-4" style={{ background: 'rgba(37,211,102,0.07)', border: '1px solid rgba(37,211,102,0.18)' }}>
                  <p className="text-sm font-medium">{t('editor.whatsappStatusMobileTitle')}</p>
                  <p className="text-xs text-[var(--text-muted)] mt-1">{t('editor.whatsappStatusMobileHint')}</p>
                  <button
                    type="button"
                    className="btn-gradient w-full mt-3"
                    data-testid="whatsapp-status-share-btn"
                    onClick={shareWhatsappStatus}
                    disabled={whatsappStatusBusy}
                  >
                    <WhatsappLogo size={16} weight="fill" />
                    {whatsappStatusBusy ? t('editor.whatsappStatusPreparing') : t('editor.whatsappStatusShare')}
                  </button>
                </div>

                <div className="rounded-xl p-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                  <p className="text-sm font-medium">{t('editor.whatsappStatusDesktopTitle')}</p>
                  <p className="text-xs text-[var(--text-muted)] mt-1">{t('editor.whatsappStatusDesktopHint')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                    <button
                      type="button"
                      className="btn-ghost"
                      data-testid="whatsapp-status-download-btn"
                      onClick={downloadWhatsappStatusPack}
                      disabled={whatsappStatusBusy}
                    >
                      <Download size={15} /> {t('editor.whatsappStatusDownload')}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => window.open('https://web.whatsapp.com/', '_blank', 'noopener,noreferrer')}
                    >
                      <ArrowSquareOut size={15} /> {t('editor.whatsappStatusOpenWeb')}
                    </button>
                  </div>
                </div>

                <p className="text-[10px] leading-relaxed text-[var(--text-muted)]">
                  {t('editor.whatsappStatusPrivacyNote')}
                </p>
              </>
            )}
          </div>
        </motion.div>
      </div>
    )}

    {/* Media Library Modal */}
    {showLibrary && (
      <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setShowLibrary(false)}>
        <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full sm:max-w-2xl max-h-[80vh] flex flex-col rounded-t-2xl sm:rounded-xl overflow-hidden" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)] flex-shrink-0">
            <p className="font-semibold text-sm flex items-center gap-2"><Images size={16} color="#a855f7" /> {t('library.title')} — {project.name}</p>
            <button onClick={() => setShowLibrary(false)} className="btn-ghost p-1"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {libraryLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-[var(--accent-purple)] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {(() => {
              // CAROUSEL-REFERENCE-DRIVE-LIBRARY-001: carousel reference
              // assets are images only (CAROUSEL_REFERENCE_ALLOWED_EXTS on
              // the backend already rejects video) — filter the picker to
              // images only in that mode instead of letting a video pick
              // fail server-side after the user already chose it.
              const displayedItems = libraryTargetMode === 'carousel_reference'
                ? libraryItems.filter(item => item.type === 'image')
                : libraryItems;
              if (!libraryLoading && displayedItems.length === 0) {
                return <p className="text-sm text-[var(--text-muted)] text-center py-12">{t('library.empty')}</p>;
              }
              if (!libraryLoading && displayedItems.length > 0) {
                return (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {displayedItems.map(item => (
                      <div key={item.id} className="relative group aspect-square rounded-lg overflow-hidden cursor-pointer" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }} onClick={() => (libraryTargetMode === 'carousel_reference' ? addFromLibraryAsCarouselReference(item) : addFromLibrary(item))}>
                        {item.type === 'image' ? (
                          <img src={resolveAssetUrl(item.url)} alt={item.original_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                            <Video size={24} color="var(--text-muted)" />
                            <p className="text-[9px] text-[var(--text-muted)] text-center px-1 truncate w-full">{item.original_name}</p>
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(168,85,247,0.6)' }}>
                          <Plus size={24} color="white" weight="bold" />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </motion.div>
      </div>
    )}

    {HEYGEN_FEATURE_ENABLED && heyGenWorkbench && (
      <div
        className="fixed inset-0 z-[240] flex items-end sm:items-center justify-center p-0 sm:p-4"
        style={{ background: 'rgba(0,0,0,0.76)', backdropFilter: 'blur(6px)' }}
        onMouseDown={event => { if (event.target === event.currentTarget) setHeyGenWorkbench(null); }}
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="w-full sm:max-w-3xl max-h-[90vh] flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden"
          style={{ background: 'var(--bg-primary)', border: '1px solid rgba(34,211,238,0.24)' }}
          onMouseDown={event => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-[var(--border-color)]">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-cyan-300">{t('editor.heygenEyebrow')}</p>
              <h3 className="font-semibold mt-1 flex items-center gap-2"><Video size={18} weight="fill" color="#67e8f9" /> {t('editor.heygenTitle')}</h3>
              <p className="text-xs text-[var(--text-muted)] mt-1">{t('editor.heygenDescription')}</p>
            </div>
            <button className="btn-ghost p-2" onClick={() => setHeyGenWorkbench(null)}><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {heyGenWorkbench.tools.length === 0 ? (
              <div className="py-10 text-center">
                <Video size={32} className="mx-auto text-[var(--text-muted)] mb-3" />
                <p className="text-sm text-[var(--text-muted)]">{t('editor.heygenNoTools')}</p>
              </div>
            ) : (
              <>
                <label className="block">
                  <span className="text-xs text-[var(--text-muted)]">{t('editor.heygenTool')}</span>
                  <select
                    className="input-dark w-full mt-2 p-3"
                    value={heyGenSelectedTool}
                    onChange={event => selectHeyGenTool(event.target.value)}
                  >
                    {heyGenWorkbench.tools.map(tool => <option key={tool.name} value={tool.name}>{tool.name}</option>)}
                  </select>
                </label>
                <div className="rounded-xl border border-cyan-300/15 bg-cyan-300/[0.04] p-3">
                  <p className="text-xs leading-relaxed text-[var(--text-muted)]">
                    {heyGenWorkbench.tools.find(tool => tool.name === heyGenSelectedTool)?.description || t('editor.heygenNoDescription')}
                  </p>
                </div>
                <label className="block">
                  <span className="text-xs text-[var(--text-muted)]">{t('editor.heygenParameters')}</span>
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">{t('editor.heygenParametersHint')}</p>
                  <textarea
                    className="input-dark font-mono text-xs min-h-56 w-full mt-2 p-3"
                    value={heyGenArguments}
                    onChange={event => setHeyGenArguments(event.target.value)}
                    spellCheck={false}
                  />
                </label>
                {heyGenResult && (() => {
                  const urls = resultUrls(heyGenResult);
                  return (
                    <div className="rounded-xl border border-[var(--border-color)] bg-black/20 p-3">
                      <p className="text-xs font-semibold mb-2">{t('editor.heygenResult')}</p>
                      {urls.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {urls.map((url, index) => (
                            <a
                              key={url}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-semibold"
                              style={{ color: '#67e8f9', background: 'rgba(34,211,238,0.09)', border: '1px solid rgba(34,211,238,0.22)' }}
                            >
                              <ArrowSquareOut size={13} /> {t('editor.heygenOpenResult', { count: index + 1 })}
                            </a>
                          ))}
                        </div>
                      )}
                      <pre className="text-[10px] whitespace-pre-wrap break-words max-h-44 overflow-auto text-[var(--text-muted)]">
                        {JSON.stringify(heyGenResult, null, 2)}
                      </pre>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-[var(--border-color)]">
            <button
              className="btn-ghost text-xs"
              onClick={() => selectHeyGenTool(heyGenSelectedTool)}
              disabled={!heyGenSelectedTool || heyGenBusy}
            >
              <ArrowClockwise size={14} /> {t('editor.heygenRefill')}
            </button>
            <button
              className="btn-gradient text-xs"
              onClick={executeHeyGenTool}
              disabled={!heyGenSelectedTool || heyGenBusy}
            >
              {heyGenBusy
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Video size={14} weight="fill" />}
              {heyGenBusy ? t('editor.heygenCreating') : t('editor.heygenGenerate')}
            </button>
          </div>
        </motion.div>
      </div>
    )}

    {/* Regeneration objective miniwizard (replaces window.confirm, CONTENT-REGEN-004) */}
    <ContentRegenerationModal
      open={showRegenerateModal}
      currentPillar={content.pillar}
      regenerating={regenerating}
      error={regenerateError}
      onCancel={closeRegenerateModal}
      onConfirm={confirmRegenerate}
    />

    {/* Input Modal (replaces window.prompt) */}
    {inputModal && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setInputModal(null)}>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="card w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
          <h3 className="font-semibold text-base mb-3">{inputModal.title}</h3>
          {inputModal.isImagePrompt && (
            <button
              className="btn-ghost text-xs py-1 px-2 mb-2 w-full"
              disabled={optimizingPrompt}
              onClick={async () => {
                const el = document.getElementById('input-modal-field');
                const current = el.value.trim();
                if (!current) return;
                setOptimizingPrompt(true);
                try {
                  const { data } = await api.post('/media/optimize-prompt', {
                    visual_direction: current,
                    script: editScript || '',
                    caption: richTextToPlainText(editCaption) || '',
                    hook_text: content.hook_text || '',
                    content_format: content.format || '',
                    project_id: project.id,
                  });
                  el.value = data.prompt;
                } catch(e) { toast.error(t('editor.optimizeError', { message: e.response?.data?.detail || e.message })); }
                setOptimizingPrompt(false);
              }}
            >
              {optimizingPrompt ? '...' : `🤖 ${t('editor.optimizeForAi')}`}
            </button>
          )}
          {inputModal.multiline
            ? <textarea className="input-dark w-full mb-3" rows={4} placeholder={inputModal.placeholder}
                defaultValue={inputModal.value}
                id="input-modal-field"
                style={{ paddingLeft: '0.75rem', paddingTop: '0.5rem', resize: 'vertical' }} />
            : <input className="input-dark w-full mb-3" placeholder={inputModal.placeholder}
                defaultValue={inputModal.value}
                id="input-modal-field"
                style={{ paddingLeft: '0.75rem' }} />
          }
          <div className="flex gap-2">
            <button className="btn-ghost flex-1" onClick={() => setInputModal(null)}>{t('common.cancel')}</button>
            <button className="btn-gradient flex-1" onClick={() => {
              const soggetto = document.getElementById('input-modal-field').value.trim();
              if (!soggetto) return;
              setInputModal(null);
              inputModal.onConfirm(soggetto);
            }}>{t('editor.generateAction')}</button>
          </div>
        </motion.div>
      </div>
    )}
    {showAiCarouselStudio && (
      <div className="fixed inset-0 z-[240] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={() => !generatingImage && setShowAiCarouselStudio(false)}>
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="w-full max-w-lg rounded-2xl overflow-hidden"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}
          onClick={e => e.stopPropagation()}
          data-testid="carousel-ai-modal"
        >
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[var(--border-color)]">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm">{t('editor.carouselAiTitle')}</h3>
                <ContextHelpButton articleId="carousel-ai" label={t('editor.carouselAiHelp')} />
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                {t('editor.carouselAiSubtitle', { count: carouselSlides.length })}
              </p>
            </div>
            <button className="btn-ghost p-1.5" onClick={() => setShowAiCarouselStudio(false)} disabled={generatingImage}><X size={16} /></button>
          </div>
          <div className="p-5 space-y-3">
            {[
              ['text_images', 'carouselAiModeTextImages', 'carouselAiModeTextImagesHint'],
              ['text_only', 'carouselAiModeTextOnly', 'carouselAiModeTextOnlyHint'],
            ].map(([mode, labelKey, hintKey]) => (
              <button
                key={mode}
                type="button"
                data-testid={`carousel-ai-mode-${mode}`}
                aria-pressed={aiCarouselMode === mode}
                className={`w-full rounded-xl p-4 text-left transition-all ${aiCarouselMode === mode ? 'ring-2 ring-[var(--gradient-start)]' : ''}`}
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
                onClick={() => setAiCarouselMode(mode)}
                disabled={generatingImage}
              >
                <p className="text-sm font-semibold">{t(`editor.${labelKey}`)}</p>
                <p className="text-xs text-[var(--text-muted)] mt-1">{t(`editor.${hintKey}`)}</p>
              </button>
            ))}
            <div className="rounded-xl px-3 py-2 text-xs text-[var(--text-muted)]" style={{ background: 'rgba(255,255,255,0.035)' }}>
              {t('editor.carouselAiExactCount', { count: carouselSlides.length })}
            </div>
          </div>
          <div className="flex gap-3 px-5 pb-5">
            <button className="btn-ghost flex-1" onClick={() => setShowAiCarouselStudio(false)} disabled={generatingImage}>{t('common.cancel')}</button>
            <button
              className="btn-gradient flex-1"
              type="button"
              data-testid="carousel-ai-generate-btn"
              disabled={generatingImage}
              onClick={async () => {
                setGeneratingImage(true);
                const tid = toast.loading(t('editor.carouselGenerating'));
                try {
                  const savedContent = await persistEditableContent();
                  const response = await api.post('/media/generate-carousel-slides', {
                    content_id: content.id,
                    project_id: project.id,
                    model: enforcedVisualModel,
                    style: 'elegant',
                    slides_count: carouselSlides.length,
                    generation_mode: aiCarouselMode,
                  });
                  const data = response.data;
                  const newItems = data?.items || [];
                  const preservedMedia = (savedContent.media || []).filter(item => !isGeneratedCarouselMedia(item));
                  const updated = { ...savedContent, media: [...preservedMedia, ...newItems] };
                  setContent(updated);
                  onUpdate?.(updated);
                  setShowAiCarouselStudio(false);
                  toast.success(t('editor.carouselGenerated', { count: newItems.length }), { id: tid });
                } catch (e) {
                  toast.error(t('editor.carouselGenerationError', { message: e.response?.data?.detail || e.message }), { id: tid });
                } finally {
                  setGeneratingImage(false);
                }
              }}
            >
              {generatingImage ? t('editor.carouselGenerating') : t('editor.generateAction')}
            </button>
          </div>
        </motion.div>
      </div>
    )}
    {showCarouselStudio && hasPremiumVisualRender && (
      <div className="fixed inset-0 z-[240] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={() => setShowCarouselStudio(false)}>
        <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full max-w-3xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
            <div>
              <h3 className="font-semibold text-sm">{t('editor.carouselStudioTitle')}</h3>
              <p className="text-xs text-[var(--text-muted)] mt-1">{t('editor.carouselStudioSubtitle', { count: carouselSlides.length })}</p>
            </div>
            <button className="btn-ghost p-1.5" onClick={() => setShowCarouselStudio(false)}><X size={16} /></button>
          </div>
          <div className="p-5 space-y-5 overflow-y-auto">
            {designDocumentCarouselRenderEnabled ? (
              <>
                <div>
                  <p className="text-xs text-[var(--text-muted)] mb-2">{t('editor.designDocumentCarouselPresetLabel')}</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="design-document-carousel-preset-picker">
                    {availableDesignDocumentCarouselPresets.map(presetKey => {
                      const preview = previewForBackgroundMode(presetKey, carouselBackgroundMode);
                      return (
                        <button
                          key={presetKey}
                          type="button"
                          data-testid={`design-document-carousel-preset-${presetKey}`}
                          aria-pressed={designDocumentCarouselPreset === presetKey}
                          onClick={() => setDesignDocumentCarouselPreset(presetKey)}
                          className={`rounded-xl p-3 text-left transition-all ${designDocumentCarouselPreset === presetKey ? 'ring-2 ring-[var(--gradient-start)]' : ''}`}
                          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
                        >
                          <div
                            data-testid={`design-document-carousel-preset-preview-${presetKey}`}
                            className={`h-20 rounded-lg mb-3 p-3 flex flex-col ${preview.align === 'center' ? 'items-center text-center justify-center' : 'justify-end'}`}
                            style={{ background: preview.background, color: preview.ink, fontFamily: preview.font }}
                          >
                            {preview.accent && (
                              <div style={{ width: preview.align === 'center' ? '18px' : '20px', height: '2px', background: preview.accent, marginBottom: '6px' }} />
                            )}
                            <p className="text-[11px] font-bold leading-tight" style={{ fontFamily: preview.font }}>
                              {t(`editor.designDocumentCarouselPreset.${presetKey}.label`)}
                            </p>
                          </div>
                          <p className="text-xs font-semibold">{t(`editor.designDocumentCarouselPreset.${presetKey}.label`)}</p>
                          <p className="text-[10px] text-[var(--text-muted)] mt-1">{t(`editor.designDocumentCarouselPreset.${presetKey}.hint`)}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2 gap-3">
                    <label className="text-xs text-[var(--text-muted)]">{t('editor.carouselSlidesLabel')}</label>
                    <p className="text-[10px] text-[var(--text-muted)]">{t('editor.carouselSlidesToGenerate', { count: carouselSlidesCount })}</p>
                  </div>
                  <input
                    type="range"
                    min={4}
                    max={8}
                    value={carouselSlidesCount}
                    onChange={e => setCarouselSlidesCount(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                {designDocumentCarouselPreset !== 'auto' && (
                  <div>
                    <label className="text-xs text-[var(--text-muted)] mb-2 block">{t('editor.carouselBackgroundModeLabel')}</label>
                    <div className="flex gap-2" data-testid="design-document-carousel-background-mode-picker">
                      {['light', 'dark'].map(mode => (
                        <button
                          key={mode}
                          type="button"
                          data-testid={`design-document-carousel-background-mode-${mode}`}
                          aria-pressed={carouselBackgroundMode === mode}
                          onClick={() => setCarouselBackgroundMode(mode)}
                          className={`flex-1 rounded-lg py-2 text-xs font-semibold transition-all ${carouselBackgroundMode === mode ? 'ring-2 ring-[var(--gradient-start)]' : ''}`}
                          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
                        >
                          {t(`editor.carouselBackgroundMode${mode === 'light' ? 'Light' : 'Dark'}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : projectDesignDnaActive ? (
              <div className="rounded-xl p-4" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.24)' }}>
                <p className="text-xs font-semibold" style={{ color: 'var(--accent-green)' }}>{t('editor.projectDesignDnaActive')}</p>
                <p className="text-xs mt-2 text-[var(--text-muted)]">{t('editor.projectDesignDnaLocked')}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="badge text-[10px]" style={{ background: 'rgba(255,255,255,0.06)' }}>◎ OpenAI</span>
                  <span className="badge text-[10px]" style={{ background: 'rgba(255,255,255,0.06)' }}>{t('editor.projectDesignDnaBadge')}</span>
                </div>
              </div>
            ) : isAdminCarouselTemplateActive ? (
              <div className="rounded-xl p-4" style={{ background: 'rgba(199,154,62,0.08)', border: '1px solid rgba(199,154,62,0.24)' }}>
                <p className="text-xs font-semibold" style={{ color: '#f4d28b' }}>{t('editor.adminCarouselTemplateActive')}</p>
                <p className="text-xs mt-2 text-[var(--text-muted)]">{t('editor.adminCarouselTemplateLocked')}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="badge text-[10px]" style={{ background: 'rgba(255,255,255,0.06)' }}>◎ OpenAI</span>
                  <span className="badge text-[10px]" style={{ background: 'rgba(255,255,255,0.06)' }}>8 slide</span>
                  <span className="badge text-[10px]" style={{ background: 'rgba(255,255,255,0.06)' }}>{t('editor.adminCarouselTemplateName')}</span>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <p className="text-xs text-[var(--text-muted)] mb-2">{t('editor.engine')}</p>
                  <div className="flex gap-2 flex-wrap">
                    <button className="preset-btn text-xs py-1 px-3 active" type="button" disabled>
                      {hasPremiumVisualRender ? '◎ OpenAI GPT Image' : `✦ ${t('editor.standardImageEngine')}`}
                    </button>
                    <span className="text-[10px] text-[var(--text-muted)] self-center">
                      {hasPremiumVisualRender ? 'Strategist render · GPT Image' : t('editor.standardImageEngineHint')}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2 gap-3">
                    <p className="text-xs text-[var(--text-muted)]">{t('editor.carouselStylePreset')}</p>
                    <p className="text-[10px] text-[var(--text-muted)]">{t('editor.carouselSlidesToGenerate', { count: carouselSlidesCount })}</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {CAROUSEL_STYLE_PRESETS.map(preset => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => setCarouselStylePreset(preset.id)}
                        className={`rounded-xl p-3 text-left transition-all ${carouselStylePreset === preset.id ? 'ring-2 ring-[var(--gradient-start)]' : ''}`}
                        style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
                      >
                        <div className="h-24 rounded-lg mb-3 p-3 flex flex-col justify-between" style={{ background: preset.tone, color: preset.accent }}>
                          <span className="text-[10px] font-semibold uppercase tracking-wide">Slide 1</span>
                          <div>
                            <p className="text-sm font-bold leading-tight">{t(`editor.carouselStyle.${preset.id}.title`)}</p>
                            <p className="text-[10px] opacity-80 mt-1">{t(`editor.carouselStyle.${preset.id}.hint`)}</p>
                          </div>
                        </div>
                        <p className="text-xs font-semibold">{t(`editor.carouselStyle.${preset.id}.label`)}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[var(--text-muted)] block mb-2">{t('editor.carouselSlidesLabel')}</label>
                  <input
                    type="range"
                    min={4}
                    max={Math.min(8, Math.max(4, carouselSlides.length))}
                    value={carouselSlidesCount}
                    onChange={e => setCarouselSlidesCount(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </>
            )}
            <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid var(--border-color)' }}>
              <div>
                <p className="text-xs font-semibold">{t('editor.carouselReferenceAssets')}</p>
                <p className="text-[11px] text-[var(--text-muted)] mt-1">{t('editor.carouselReferenceAssetsHint')}</p>
              </div>
              {/* CAROUSEL-REFERENCE-DRIVE-LIBRARY-001: three sources for the
                  same per-slide reference-image list — local upload
                  (unchanged), Google Drive and the project's own Media
                  Library, both already used elsewhere in this editor and
                  now also routed here via openDrivePicker/openLibrary's
                  'carousel_reference' target. */}
              <div className="flex flex-wrap gap-2 mt-3">
                <label className={`btn-ghost text-xs px-3 py-2 whitespace-nowrap cursor-pointer ${uploadingCarouselReference ? 'opacity-60 pointer-events-none' : ''}`}>
                  <Plus size={14} className="inline mr-1" />
                  {uploadingCarouselReference ? t('editor.carouselReferenceUploading') : t('editor.carouselReferenceUpload')}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={e => {
                      uploadCarouselReferenceAssets(e.target.files);
                      e.target.value = '';
                    }}
                  />
                </label>
                <button
                  type="button"
                  className={`btn-ghost text-xs px-3 py-2 whitespace-nowrap ${uploadingCarouselReference ? 'opacity-60 pointer-events-none' : ''}`}
                  disabled={uploadingCarouselReference}
                  onClick={() => openDrivePicker('carousel_reference')}
                  data-testid="carousel-reference-drive-btn"
                >
                  <Download size={14} weight="bold" className="inline mr-1" /> {t('editor.googleDrive')}
                </button>
                <button
                  type="button"
                  className={`btn-ghost text-xs px-3 py-2 whitespace-nowrap ${uploadingCarouselReference ? 'opacity-60 pointer-events-none' : ''}`}
                  disabled={uploadingCarouselReference}
                  onClick={() => openLibrary('carousel_reference')}
                  data-testid="carousel-reference-library-btn"
                >
                  <Images size={14} className="inline mr-1" /> {t('editor.mediaLibraryShort')}
                </button>
              </div>
              {carouselReferenceAssets.length === 0 ? (
                <p className="text-[11px] text-[var(--text-muted)] mt-4">{t('editor.carouselReferenceEmpty')}</p>
              ) : (
                <div className="mt-4 space-y-2">
                  {carouselReferenceAssets.map(asset => (
                    <div key={asset.id} className="flex items-center gap-3 rounded-lg p-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
                      <img src={resolveAssetUrl(asset.url)} alt="" className="w-12 h-12 object-cover rounded-md flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs truncate">{asset.original_name}</p>
                        <p className="text-[10px] text-[var(--text-muted)] mt-1">{t('editor.carouselReferenceSlide')}</p>
                      </div>
                      <select
                        className="input-dark text-xs py-1.5 px-2 w-24"
                        value={asset.slide_position || '1'}
                        onChange={e => updateCarouselReferencePosition(asset.id, e.target.value)}
                      >
                        {Array.from({ length: carouselSlidesCount }, (_, index) => (
                          <option key={index + 1} value={String(index + 1)}>{index + 1}</option>
                        ))}
                        <option value="last">{t('editor.carouselReferenceLast')}</option>
                      </select>
                      <button className="btn-ghost p-1.5" type="button" onClick={() => deleteCarouselReferenceAsset(asset.id)} title={t('common.delete')}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <p className="text-xs text-[var(--text-muted)] mb-2">{t('editor.carouselPreviewSlides')}</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {carouselSlides.slice(0, carouselSlidesCount).map((slide, idx) => (
                  <div key={idx} className="text-xs rounded-lg px-3 py-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
                    <span className="font-semibold text-[var(--text-primary)]">{t('editor.slideN', { count: idx + 1 })}</span>
                    <p className="text-[var(--text-muted)] mt-1 whitespace-pre-wrap">{slide.slice(0, 220)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3 px-5 pb-5">
            <button className="btn-ghost flex-1" onClick={() => setShowCarouselStudio(false)} disabled={generatingImage}>{t('common.cancel')}</button>
            <button className="btn-gradient flex-1" disabled={generatingImage} data-testid="carousel-generate-btn" onClick={async () => {
              setGeneratingImage(true);
              const tid = toast.loading(t('editor.carouselGenerating'));
              try {
                const savedContent = await persistEditableContent();
                if (designDocumentCarouselRenderEnabled) {
                  const assets = carouselReferenceAssets.map(asset => buildDesignDocumentCarouselAsset(asset, carouselSlidesCount));
                  await api.post('/media/render-carousel-design-document', {
                    content_id: content.id,
                    project_id: project.id,
                    slides_count: carouselSlidesCount,
                    preset_key: designDocumentCarouselPreset,
                    background_mode: carouselBackgroundMode,
                    assets,
                  });
                  // CAROUSEL-JOB-BACKGROUND-NOTIFY-001 (2026-08-13): the render
                  // itself runs as a detached BackgroundTask server-side (see
                  // carousel_render_job_service.py) — it is NOT tied to this
                  // request, and doesn't need to be tied to this modal staying
                  // open either. The old code blocked here for up to 180s
                  // polling /contents, which (a) forced the user to babysit an
                  // open modal and (b) genuinely wasn't enough time for a full
                  // multi-slide freeform render (sequential per-slide vision-AI
                  // calls + sequential per-slide Puppeteer renders), so it threw
                  // a "taking longer than expected" error on totally healthy,
                  // still-running jobs. We now fire-and-forget: close the modal
                  // right away, and let ProjectView's own 8s /contents poll
                  // (already running for every other reason, see
                  // checkCarouselJobTransitions in ProjectView.js) pick up the
                  // job's done/failed transition and raise the real notification
                  // whenever it actually happens — even if this modal is closed
                  // or the user has navigated to a different tab by then.
                  setShowCarouselStudio(false);
                  toast.success(t('editor.carouselGenerationStarted'), { id: tid });
                } else {
                  const response = await api.post('/media/generate-carousel-slides', {
                    content_id: content.id,
                    project_id: project.id,
                    model: enforcedVisualModel,
                    style: projectDesignDnaActive ? 'project_dna' : carouselStylePreset,
                    slides_count: carouselSlidesCount,
                  });
                  const data = response.data;
                  const newItems = data?.items || [];
                  const preservedMedia = (savedContent.media || []).filter(item => !isGeneratedCarouselMedia(item));
                  const updated = { ...savedContent, media: [...preservedMedia, ...newItems] };
                  setContent(updated);
                  onUpdate?.(updated);
                  setShowCarouselStudio(false);
                  toast.success(t('editor.carouselGenerated', { count: newItems.length }), { id: tid });
                }
              } catch (e) {
                // CAROUSEL-JOB-BACKGROUND-NOTIFY-001: carouselJobTimeout/
                // carouselJobFailed can no longer be thrown from here (the
                // blocking poll that produced them is gone) — this branch
                // now only ever sees a genuine failure to even START the
                // job (network error, validation 422 on submission, etc.).
                const message = e.response?.data?.detail || e.message;
                if (e.response?.status === 422) {
                  toast.error(t('editor.carouselGenerationTextOverflowError', { message }), { id: tid });
                } else {
                  toast.error(t('editor.carouselGenerationError', { message }), { id: tid });
                }
              }
              setGeneratingImage(false);
            }}>
              {generatingImage ? '...' : t('editor.generateCarouselAction')}
            </button>
          </div>
        </motion.div>
      </div>
    )}
    {/* Navigable lightbox */}
    {lightboxIdx !== null && (() => {
      const imgMedia = (content.media || []).filter(m => m.type === 'image');
      const cur = imgMedia[lightboxIdx];
      if (!cur) return null;
      return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.92)' }} onClick={() => setLightboxIdx(null)}>
          <button className="absolute top-4 right-4 btn-ghost p-2" onClick={() => setLightboxIdx(null)}><X size={24} /></button>
          {imgMedia.length > 1 && (
            <button className="absolute left-4 top-1/2 -translate-y-1/2 btn-ghost p-2 z-10" onClick={e => { e.stopPropagation(); setLightboxIdx(i => (i - 1 + imgMedia.length) % imgMedia.length); }}>
              <CaretLeft size={28} weight="bold" />
            </button>
          )}
          <img src={resolveAssetUrl(cur.url)} alt="" className="max-w-full max-h-full rounded-xl object-contain" style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()} />
          {imgMedia.length > 1 && (
            <>
              <button className="absolute right-4 top-1/2 -translate-y-1/2 btn-ghost p-2 z-10" onClick={e => { e.stopPropagation(); setLightboxIdx(i => (i + 1) % imgMedia.length); }}>
                <CaretRight size={28} weight="bold" />
              </button>
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-1.5">
                {imgMedia.map((_, i) => (
                  <div key={i} onClick={e => { e.stopPropagation(); setLightboxIdx(i); }} className="w-2 h-2 rounded-full cursor-pointer transition-all" style={{ background: i === lightboxIdx ? 'white' : 'rgba(255,255,255,0.35)' }} />
                ))}
              </div>
            </>
          )}
        </div>
      );
    })()}

    {/* Schedule overlay — centered */}
    {showSchedule && calViewDate && (() => {
      const todayD = new Date();
      return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={() => { setShowSchedule(false); setCustomPerSocial(false); }}>
        <div className="w-full rounded-2xl overflow-hidden" style={{ maxWidth: 400, background: 'var(--bg-card)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()}>
          <div className="p-5 pb-0">
            {/* Date header */}
            <p className="text-sm text-[var(--text-muted)] mb-0.5">{calViewDate.getFullYear()}</p>
            <p className={`text-2xl font-bold ${scheduleDate ? 'mb-4' : 'mb-1'}`} style={{ color: scheduleDate ? 'var(--text-primary)' : 'var(--text-muted)' }}>{formatScheduleHeader(scheduleDate, locale, t)}</p>
            {!scheduleDate && <p className="text-xs mb-4" style={{ color: '#f59e0b' }}>← {t('editor.selectDate')}</p>}

            {/* Calendar */}
            <div className="rounded-xl mb-4 p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className="flex items-center justify-between mb-3 px-1">
                <button className="btn-ghost p-1" onClick={() => setCalViewDate(d => new Date(d.getFullYear(), d.getMonth()-1, 1))}><CaretLeft size={16} /></button>
                <button className="btn-ghost p-1" onClick={() => setCalViewDate(d => new Date(d.getFullYear(), d.getMonth()+1, 1))}><CaretRight size={16} /></button>
              </div>
              <div className="grid grid-cols-7 mb-2">
                {dayHeaders.map(d => <div key={d} className="text-center text-xs font-bold text-[var(--text-muted)] py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {calDaysExtended(calViewDate).map((cell, i) => {
                  const iso = `${cell.year}-${String(cell.month+1).padStart(2,'0')}-${String(cell.day).padStart(2,'0')}`;
                  const isSelected = scheduleDate === iso;
                  const isToday = cell.isCurrent && cell.day === todayD.getDate() && cell.month === todayD.getMonth() && cell.year === todayD.getFullYear();
                  const isPast = cell.isCurrent && new Date(cell.year, cell.month, cell.day) < new Date(todayD.getFullYear(), todayD.getMonth(), todayD.getDate());
                  const dayIndicator = scheduledDateIndicators[iso];
                  const indicatorPlatforms = dayIndicator ? Array.from(dayIndicator.platforms).slice(0, 4) : [];
                  const TEAL = '#14b8a6';
                  return (
                    <button key={i} onClick={() => !cell.isPrev && setScheduleDate(iso)}
                      className="flex flex-col items-center py-1 transition-all"
                      style={{ cursor: cell.isPrev ? 'default' : 'pointer' }}>
                      {isToday && <span style={{ fontSize: 8, color: TEAL, fontWeight: 700, lineHeight: 1.2 }}>{t('editor.today')}</span>}
                      {cell.isNext && <span style={{ fontSize: 8, color: '#f59e0b', fontWeight: 700, lineHeight: 1.2 }}>{capitalize(new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(cell.year, cell.month, 1)))}</span>}
                      {!isToday && !cell.isNext && <span style={{ fontSize: 8, lineHeight: 1.2 }}>&nbsp;</span>}
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: isToday || isSelected ? 700 : 500,
                        background: isToday || isSelected ? TEAL : 'transparent',
                        color: isToday || isSelected ? 'white' : cell.isPrev ? 'rgba(255,255,255,0.2)' : isPast ? 'rgba(255,255,255,0.35)' : 'var(--text-primary)',
                      }}>
                        {cell.day}
                      </div>
                      <div className="h-2 mt-0.5 flex items-center justify-center gap-0.5">
                        {indicatorPlatforms.map(platform => (
                          <span
                            key={platform}
                            className="w-1.5 h-1.5 rounded-full"
                            title={PLATFORM_ICONS[platform]?.name || platform}
                            style={{
                              background: PLATFORM_ICONS[platform]?.color || '#f97316',
                              boxShadow: isSelected ? '0 0 0 1px rgba(255,255,255,0.35)' : 'none',
                            }}
                          />
                        ))}
                        {dayIndicator && indicatorPlatforms.length === 0 && (
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#f97316' }} />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedDateOverlapCount > 0 && (
              <div className="rounded-xl mb-4 px-3 py-2 text-xs leading-relaxed" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.28)', color: '#fbbf24' }}>
                {t('editor.scheduleOverlapWarning', { count: selectedDateOverlapCount })}
              </div>
            )}

            {/* Time section */}
            {!customPerSocial ? (
              <>
                <p className="text-sm text-[var(--text-muted)] mb-2">{t('editor.time')}</p>
                <input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                  className="w-full text-center text-xl font-semibold rounded-xl mb-4"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-color)', padding: '14px', color: 'var(--text-primary)' }} />
                <button className="w-full flex items-center justify-center gap-2 text-sm rounded-xl mb-3 py-3 transition-colors"
                  style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)', color: 'var(--text-primary)' }}
                  onClick={applyBestTimeSuggestion}>
                  <Sparkle size={14} />
                  {t('editor.suggestBestTime')}
                </button>
                <button className="w-full text-center text-sm text-[var(--text-muted)] mb-4 hover:text-white transition-colors"
                  onClick={() => setCustomPerSocial(true)}>
                  {t('editor.customizeTimePerSocial')} <span style={{ opacity: 0.6 }}>ⓘ</span>
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-[var(--text-muted)] mb-3">{t('editor.selectTimePerSocial')} <span style={{ opacity: 0.6 }}>ⓘ</span></p>
                {selectedProfiles.map(prof => {
                  const pi = PLATFORM_ICONS[prof.platform] || { Icon: Globe, color: '#888' };
                  return (
                    <div key={prof.id} className="flex items-center gap-3 mb-3">
                      <div className="relative flex-shrink-0">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
                          style={{ background: `${pi.color}30`, border: `2px solid ${pi.color}60`, color: pi.color }}>
                          {(prof.profile_name || '?')[0].toUpperCase()}
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: pi.color }}>
                          <pi.Icon size={10} color="white" weight="fill" />
                        </div>
                      </div>
                      <div className="flex-1 flex items-center justify-between rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-color)' }}>
                        <input type="time" value={socialTimes[prof.id] || scheduleTime}
                          onChange={e => setSocialTimes(prev => ({ ...prev, [prof.id]: e.target.value }))}
                          className="text-base font-semibold bg-transparent border-0 outline-none flex-1 text-center"
                          style={{ color: 'var(--text-primary)', minWidth: 0 }} />
                      </div>
                      <button className="btn-ghost p-1.5 flex-shrink-0" onClick={() => setSocialTimes(prev => ({ ...prev, [prof.id]: scheduleTime }))}>
                        <PencilSimple size={14} />
                      </button>
                    </div>
                  );
                })}
                <button className="w-full flex items-center justify-center gap-2 text-sm rounded-xl mb-3 py-3 transition-colors"
                  style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)', color: 'var(--text-primary)' }}
                  onClick={applyBestTimeSuggestion}>
                  <Sparkle size={14} />
                  {t('editor.suggestBestTime')}
                </button>
                <button className="w-full text-center text-sm text-[var(--text-muted)] mb-4 hover:text-white transition-colors"
                  onClick={() => setCustomPerSocial(false)}>
                  {t('editor.unifiedTime')}
                </button>
              </>
            )}
          </div>

          {/* Selected socials indicator */}
          <div className="px-5 pb-3">
            {actionableSelectedProfiles.length === 0 ? (
              <p className="text-xs text-center py-2 rounded-xl" style={{ color: '#f59e0b', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
                ⚠ {t('editor.noSocials')}
              </p>
            ) : (
              <div className="flex items-center gap-2 py-2 px-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <div className="flex -space-x-1.5">
                  {actionableSelectedProfiles.map(prof => {
                    const pi = PLATFORM_ICONS[prof.platform] || { Icon: Globe, color: '#888' };
                    return (
                      <div key={prof.id} className="w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-[var(--bg-card)]" style={{ background: pi.color }}>
                        <pi.Icon size={12} color="white" weight="fill" />
                      </div>
                    );
                  })}
                </div>
                <span className="text-xs text-[var(--text-muted)]">{t('editor.selectedSocialsCount', { count: actionableSelectedProfiles.length })}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 px-5 pb-5">
            <button className="btn-ghost flex-1" onClick={() => { setShowSchedule(false); setCustomPerSocial(false); }} disabled={scheduling}>{t('common.cancel')}</button>
            <button className="btn-gradient flex-1" onClick={schedule} disabled={scheduling || !scheduleDate || selectedSocials.length === 0 || !tiktokSettingsComplete}>
              {scheduling ? '...' : t('editor.scheduleBtn')}
            </button>
          </div>
        </div>
      </div>
      );
    })()}
    </>
  );
}
